const REPORT_COMMAND_PATTERN = /\b(generate|create|make|build|prepare|view|open|show|get|give|want|need|send|produce|share|pull up|download)\b[^.!?]{0,30}\breport\b|\bdiagnostic report\b|\bview full report\b/i;
const REPORT_READY_PATTERN = /\b(?:diagnostic\s+)?report\b.*\b(generated|ready|complete|created)\b|\b(?:your\s+)?report is ready\b|\breport generated\b|\bready to share\b/i;
// Matches CarFido's "the report is ready to generate" signal. Covers BOTH the legacy
// "diagnostic report" wording and the current "symptom report" wording, plus the
// "report-ready point" / "Generate Symptom Report option" phrasing the model sometimes
// uses — so auto-generation fires regardless of exact wording. The STILL_DIAGNOSING_PATTERN
// guard below excludes mid-diagnosis messages that merely mention generating the report.
const REPORT_READY_PROMPT_PATTERN = /\b(ready to generate (?:your |a |the )?(?:diagnostic|symptom) report|i have all the details i need|i have enough (?:details|information)|enough information to generate(?: your| a| the)? (?:diagnostic|symptom) report|generate the (?:diagnostic|symptom) report|reached the report[- ]?ready|report[- ]?ready point|generate (?:the |your |a )?(?:diagnostic|symptom) report option)\b/i;
const REPORT_CHOICE_PATTERN = /\b(diy steps report|diy report|diy guide|mechanic report|mechanic diagnostic report)\b/i;
const DIAGNOSIS_FINALIZED_PATTERN = /\b(based on what you'?ve told me|most likely (?:cause|it'?s|the|your|you'?re|a |an |leak|source|culprit|problem|issue|suspect|reason|failure|fault|part|component)|(?:very |highly |quite |much |even |most |more )likely\b|worn (?:down )?to (?:the )?metal|\breplacement\b|\btow(?:ed|ing)?\b|next action:|difficulty rating:|confirms? the|marked this issue as resolved|issue as resolved|this (?:now )?needs an?|needs? [^?]{0,50}(?:professional inspection|inspection and|replacement|replaced)|needs an? .{0,40}(?:check|inspection|test|repair|diagnosis) (?:at|by|from|with) a? ?(?:shop|mechanic|garage|professional|technician|dealer)|take it to (?:a )?(?:shop|mechanic|garage|professional|dealer)|recommend(?:ed|ing)? (?:a |seeing |visiting )?(?:shop|mechanic|garage|professional|dealer)|have (?:it|the|your)\b[^?]{0,60}\b(?:inspected|replaced|repaired|serviced))\b/i;
const RESOLUTION_PATTERN = /\b(confirms? the|marked this issue as resolved|issue as resolved|caused by|resolved)\b/i;
const REPAIR_GUIDANCE_PATTERN = /\b(try this first|if that doesn'?t fix|verify:|difficulty rating|step-by-step|diy repair steps|required tools|parts you may need|watch diagnostic video|turn the car fully off|clean and tighten|test battery voltage|try a jump[- ]start)\b/i;
const REPAIR_ACTION_SUGGESTION_PATTERN = /\b(work plan|repair shop|find shop|diy|mechanic|report|guide|how to test|what voltage|starter|terminal steps|add to work)\b/i;
const GENERIC_REPORT_REQUEST_PATTERN = /\b(vehicle report|work plan|workplan|pdf|report pdf|service history report|download service history|export service history)\b/i;
const VEHICLE_ISSUE_PATTERN = /\b(my car|car|vehicle|engine|battery|starter|brake|ac|a\/c|air|cooling|compressor|refrigerant|noise|click|warning|light|tire|oil|leak|smell|overheat|won'?t|will not|not cooling|not start|no start|problem|issue)\b/i;
const GENERIC_DIAGNOSIS_PROMPT_PATTERN = /^(diagnose a problem|diagnose an issue|i need help diagnosing a problem|i need help diagnosing an issue|what can you do|add my vehicle)$/i;
// A user message that actually describes a FAULT/SYMPTOM the driver is experiencing. This is
// what makes a conversation "diagnostic" — distinct from a parts-shopping, how-to, or
// service-logging chat that merely mentions a component. Kept broad and in rough lock-step with
// the backend _SYMPTOM_HINT_RE so the client fallback and the backend agree on what is a symptom.
const SYMPTOM_SIGNAL_PATTERN = /\b(won'?t|wont|will not|not (?:start|starting|cranking|cold|cooling|working|charging|turning|blowing|shifting|idling)|no[- ]?start|no crank|dead|dying|dim(?:s|ming|mer|med)?|flicker(?:s|ing|ed)?|burn(?:t|ed|ing|s)?\s*(?:out|smell)?|blown|stall(?:s|ing|ed)?|misfir(?:e|es|ing)?|rough\s+(?:idle|running)|shak(?:e|es|ing|y)|vibrat(?:e|es|ing|ion)?|wobbl(?:e|es|ing|y)?|rattl(?:e|es|ing)?|grind(?:s|ing)?|squeal(?:s|ing)?|squeak(?:s|ing)?|knock(?:s|ing)?|clunk(?:s|ing)?|click(?:s|ing)?|whin(?:e|es|ing)?|hum(?:s|ming)?|tick(?:s|ing)?|strange noise|weird noise|loud noise|leak(?:s|ing|ed)?|drip(?:s|ping)?|smok(?:e|es|ing|y)?|steam(?:ing)?|smell(?:s|ing)?|odou?r|overheat(?:s|ing|ed)?|running hot|warning light|check engine|engine light|abs light|airbag light|won'?t turn|hard to (?:start|steer|brake|turn)|hesitat(?:e|es|ing|ion)?|jerk(?:s|ing|y)?|surg(?:e|es|ing)?|slip(?:s|ping|ped)?|stuck|stops? working|stopped working|loss of power|no power|low power|pull(?:s|ing)? to the|soft (?:pedal|brake)|spongy|fade|pulsat|headlights?\s+(?:are\s+)?(?:out|dim|dying|weak)|bulb[s]?\s+(?:out|blown|burn)|won'?t move|won'?t go|cuts? out|cutting out|dies|died|hot to the touch)\b/i;
// A user message whose PRIMARY intent is a how-to, parts comparison/shopping, or
// service-logging request — NOT a fault report. Used to recognise non-diagnostic chats so the
// client never fabricates a symptom report for them (mirrors the backend not_diagnostic gate).
const NON_DIAGNOSTIC_INTENT_PATTERN = /\b(how (?:do|to|can|would|should) (?:i|you|we)|how to\b|step[- ]?by[- ]?step|install(?:ation|ing|ed)?|replace the|replacing the|removing the|how.{0,24}replace|\bvs\.?\b|versus|which (?:is|one)? ?(?:better|best)|better quality|good quality|recommend(?:ation|ed|s)?|best (?:kit|brand|product|option|one)|i (?:did|just did|already|completed|changed|replaced|installed|fitted|had|got)\b|oil change|what oil|last oil change|log (?:a |my )?service|service (?:record|history|log|done)|price of|cost of|how much (?:is|does|are|for)|where (?:can|to) (?:buy|get)|compatible|fitment|part number)\b/i;
const ATTACHMENT_MARKER_PREFIX = '__ATTACHMENTS__:';

export const stripAttachmentMetadataMarker = (value = '') => {
    const raw = String(value || '');
    if (!raw.includes(ATTACHMENT_MARKER_PREFIX)) return raw;

    let output = '';
    let cursor = 0;
    while (cursor < raw.length) {
        const markerIndex = raw.indexOf(ATTACHMENT_MARKER_PREFIX, cursor);
        if (markerIndex < 0) {
            output += raw.slice(cursor);
            break;
        }

        output += raw.slice(cursor, markerIndex).replace(/[ \t]*$/g, '');
        let end = markerIndex + ATTACHMENT_MARKER_PREFIX.length;
        const opener = raw[end];
        const closer = opener === '[' ? ']' : opener === '{' ? '}' : '';

        if (closer) {
            let depth = 0;
            let inString = false;
            let escaped = false;
            for (; end < raw.length; end += 1) {
                const char = raw[end];
                if (escaped) {
                    escaped = false;
                    continue;
                }
                if (char === '\\') {
                    escaped = true;
                    continue;
                }
                if (char === '"') {
                    inString = !inString;
                    continue;
                }
                if (inString) continue;
                if (char === opener) depth += 1;
                if (char === closer) {
                    depth -= 1;
                    if (depth === 0) {
                        end += 1;
                        break;
                    }
                }
            }
        } else {
            const lineEnd = raw.indexOf('\n', end);
            end = lineEnd >= 0 ? lineEnd : raw.length;
        }

        while (end < raw.length && /[ \t]/.test(raw[end])) end += 1;
        if (raw[end] === '\r' && raw[end + 1] === '\n') end += 2;
        else if (raw[end] === '\n' || raw[end] === '\r') end += 1;
        cursor = end;
    }

    return output;
};

const cleanText = (value = '') => stripAttachmentMetadataMarker(value)
    .replace(/\[SUGGESTIONS:[\s\S]*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const slugify = (value = '') => cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const formatDate = (date) => date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
});

const scoreToLabel = (score) => {
    if (score >= 80) return 'High';
    if (score >= 65) return 'Medium';
    return 'Low';
};

const clampScore = (score) => Math.max(45, Math.min(95, Math.round(score)));

const createForumUrl = (vehicle, causeTitle) => (
    `https://www.google.com/search?q=${encodeURIComponent(`${vehicle} ${causeTitle} forum`)}`
);

const createYoutubeUrl = (vehicle, causeTitle) => (
    `https://www.youtube.com/results?search_query=${encodeURIComponent(`${vehicle} ${causeTitle} diagnostic`)}`
);

const toAnswerText = (details = []) => details.map((item) => cleanText(item.answer).toLowerCase()).join(' ');

const normalizeDifficulty = (value = 'Medium') => {
    const normalized = cleanText(value).toLowerCase();
    if (normalized === 'easy') return 'Easy';
    if (normalized === 'hard') return 'Hard';
    return 'Medium';
};

const createTool = (name, priceLabel = '', link = null) => ({
    name,
    priceLabel,
    link,
});

const createPart = (name, notes = '') => ({
    name,
    notes,
});

const createSymptomBullet = (text) => cleanText(text).replace(/\.$/, '');

const createGuidedStep = (instruction, detail = '', toolLabel = '') => ({
    instruction,
    detail,
    toolLabel,
});

const buildDetailedRepairSteps = ({ repairSteps = [], guidedSteps = [], steps = [] }) => {
    if (repairSteps.length > 0) return repairSteps;
    if (guidedSteps.length > 0) {
        return guidedSteps.map((step) => (
            step.detail
                ? `${step.instruction} ${step.detail}`
                : step.instruction
        ));
    }
    return steps;
};

const buildDefaultSafetyPrecautions = ({ title = '', requiredTools = [] }) => {
    const lowerTitle = cleanText(title).toLowerCase();
    const toolNames = requiredTools.map((tool) => cleanText(tool.name).toLowerCase()).join(' ');
    const precautions = [
        'Park on a flat surface, switch the ignition off, and let hot components cool before starting work.',
        'Wear safety glasses and gloves, and keep loose clothing away from moving parts.',
    ];

    if (lowerTitle.includes('battery') || lowerTitle.includes('starter') || toolNames.includes('multimeter')) {
        precautions.push('Disconnect the negative battery terminal first to reduce the risk of short circuits.');
    }

    if (lowerTitle.includes('starter')) {
        precautions.push('If you need to work underneath the vehicle, support it with jack stands before reaching under it.');
    }

    if (lowerTitle.includes('corroded') || lowerTitle.includes('terminal')) {
        precautions.push('Avoid touching battery corrosion with bare hands and rinse away cleaner residue before reconnecting the battery.');
    }

    return precautions;
};

export const isReportCommand = (text = '') => REPORT_COMMAND_PATTERN.test(cleanText(text));

export const isReportChoiceText = (text = '') => REPORT_CHOICE_PATTERN.test(cleanText(text));

export const isRepairGuidanceContent = (text = '') => REPAIR_GUIDANCE_PATTERN.test(cleanText(text));

// Classify a safe-to-drive value into one of three consistent levels so every view
// styles it the same way:
//   'no'      → do NOT drive        → red
//   'caution' → drive with caution  → yellow/amber
//   'yes'     → safe to drive       → green
// Caution wins over urgency (a High-urgency issue can still be "Use caution").
export const getSafeToDriveLevel = (safeToDrive = '') => {
    const value = String(safeToDrive || '').toLowerCase();
    if (!value) return 'caution';
    if (/\b(do ?not|don'?t|never|unsafe|not safe|no\b)/.test(value)) return 'no';
    if (/caution|careful|limited|short|risk|monitor/.test(value)) return 'caution';
    if (/\byes\b|safe|ok|fine|good to go/.test(value)) return 'yes';
    return 'caution';
};

// Tailwind-ready color tokens for each safe-to-drive level, shared across the
// in-chat card and the full report page so the styling never diverges.
export const SAFE_TO_DRIVE_STYLES = {
    no: {
        border: '#fecaca', bg: '#fef2f2', icon: '#dc2626',
        heading: '#b91c1c', pillBg: '#ffe4e7', pillText: '#dc2626', body: '#414141', sub: '#414141',
    },
    caution: {
        border: '#fde68a', bg: '#fffbeb', icon: '#d97706',
        heading: '#92400e', pillBg: '#fef3c7', pillText: '#b45309', body: '#78350f', sub: '#a16207',
    },
    yes: {
        border: '#bbf7d0', bg: '#f0fdf4', icon: '#16a34a',
        heading: '#15803d', pillBg: '#dcfce7', pillText: '#15803d', body: '#0f5132', sub: '#15803d',
    },
};

export const isRepairActionSuggestion = (text = '') => (
    REPORT_CHOICE_PATTERN.test(cleanText(text)) ||
    REPAIR_ACTION_SUGGESTION_PATTERN.test(cleanText(text))
);

export const removeReportChoicePrompt = (text = '') => {
    const content = String(text || '');
    return content
        .split('\n')
        .filter((line) => {
            const lower = cleanText(line).toLowerCase();
            if (!lower) return true;
            return !(lower.includes('diy') && lower.includes('mechanic') && lower.includes('report'));
        })
        .join('\n')
        .replace(/\bwant\s+(?:a|me to generate)?\s*diy[^?]*mechanic[^?]*report\?/ig, '')
        .trim();
};

export const isDiagnosticReportMessage = (message) => {
    if (!message || message.role !== 'assistant') return false;
    if (message.diagnosticReport) return true;
    const content = cleanText(message.content).toLowerCase();
    const hasReportPdf = Boolean(message.pdfUrl) && (
        content.includes('report') ||
        content.includes('diagnostic') ||
        content.includes('ready to share')
    );
    return REPORT_READY_PATTERN.test(content) || hasReportPdf;
};

// Phrasings CarFido uses while it is STILL diagnosing (narrowing down and asking another
// question). These mention "generate the report" but are NOT a ready-to-generate signal,
// so they must never trigger report generation — otherwise the report starts building while
// a diagnostic question is still pending (e.g. "I'm narrowing this down so I can generate
// the diagnostic report. Does it start with a jump-start?").
const STILL_DIAGNOSING_PATTERN = /\bnarrow(?:ing)?\s+(?:this|it|things?|the cause)?\s*down\b|\bso (?:i|we) can generate\b|\bbefore (?:i|we) (?:can )?generate\b/i;

export const isReportReadyPromptMessage = (messageOrText) => {
    const content = typeof messageOrText === 'string'
        ? cleanText(messageOrText)
        : cleanText(messageOrText?.content);
    if (!content) return false;
    // Still narrowing down / asking a question → not ready, even if it mentions the report.
    if (STILL_DIAGNOSING_PATTERN.test(content)) return false;
    return REPORT_READY_PROMPT_PATTERN.test(content);
};

export const isDiagnosticFinalizedMessage = (message) => {
    if (!message || message.role !== 'assistant') return false;
    const content = cleanText(message.content);
    if (!content || isDiagnosticReportMessage(message)) return false;
    if (/\bQuestion:/i.test(content) && !RESOLUTION_PATTERN.test(content)) return false;
    return DIAGNOSIS_FINALIZED_PATTERN.test(content);
};

const extractQuestion = (content) => {
    const cleaned = cleanText(content);
    const questionIndex = cleaned.lastIndexOf('?');
    if (questionIndex === -1) return '';

    const beforeQuestion = cleaned.slice(0, questionIndex + 1);
    const sentenceStart = Math.max(
        beforeQuestion.lastIndexOf('. '),
        beforeQuestion.lastIndexOf('! '),
        beforeQuestion.lastIndexOf('\n')
    );

    return beforeQuestion.slice(sentenceStart + 1).trim();
};

// Soft, one-line nudge toward the symptom report. It is appended AFTER the driver's answer and
// never replaces it — mid-diagnosis we always answer the question (steps, time estimate, part
// advice, side question) first, then gently steer back. (#763)
const PRE_REPORT_SOFT_REMINDER = "We're close to your symptom report — one more detail will get us there.";

export const getPreReportDiagnosticContent = (text = '') => {
    const content = removeReportChoicePrompt(text);
    if (isReportReadyPromptMessage(content)) {
        return 'I have all the details I need. Ready to generate your diagnostic report?';
    }

    // Not repair guidance — show the answer verbatim, no checkpoint.
    if (!isRepairGuidanceContent(content)) return content;

    // A finalized diagnosis (cause confirmed / needs replacement) transitions straight to the
    // report — keep the ready prompt so auto-generation still fires.
    if (DIAGNOSIS_FINALIZED_PATTERN.test(content)) {
        return 'I have all the details I need. Ready to generate your diagnostic report?';
    }

    // CRITICAL (#763): NEVER withhold the answer behind a gate ("Repair steps will unlock…",
    // "collecting the basic diagnostic details first…"). Show the full answer the agent gave,
    // then add at most ONE short reminder line, ending on the agent's forward question so the
    // driver always has a concrete next step.
    const explicitQuestion = content.match(/\bQuestion:\s*([^?]+\?)/i);
    if (explicitQuestion?.[1]) {
        const answer = content.slice(0, explicitQuestion.index).trim();
        const lead = answer ? `${answer}\n\n` : '';
        return `${lead}${PRE_REPORT_SOFT_REMINDER}\n\nQuestion: ${explicitQuestion[1].trim()}`;
    }

    const naturalQuestion = extractQuestion(content);
    if (naturalQuestion) {
        const qIndex = content.lastIndexOf(naturalQuestion);
        const answer = qIndex > 0 ? content.slice(0, qIndex).trim() : '';
        const lead = answer ? `${answer}\n\n` : '';
        return `${lead}${PRE_REPORT_SOFT_REMINDER}\n\nQuestion: ${naturalQuestion}`;
    }

    // The answer had no follow-up question — show it in full and add the single soft-reminder line.
    return `${content.trim()}\n\n${PRE_REPORT_SOFT_REMINDER}`;
};

const isGenericDiagnosticPrompt = (content = '') => GENERIC_DIAGNOSIS_PROMPT_PATTERN.test(cleanText(content));

export const getDiagnosticStartIndex = (messages) => {
    const candidates = messages
        .map((message, index) => ({ message, index, content: cleanText(message.content) }))
        .filter(({ message, content }) => (
            message.role === 'user' &&
            content &&
            !isReportCommand(content) &&
            !isReportChoiceText(content) &&
            !GENERIC_REPORT_REQUEST_PATTERN.test(content) &&
            !isGenericDiagnosticPrompt(content)
        ));

    const issueCandidate = candidates.find(({ content }) => {
        const wordCount = content.split(/\s+/).filter(Boolean).length;
        return VEHICLE_ISSUE_PATTERN.test(content) && (wordCount > 2 || /\b(ac|a\/c|car|vehicle)\b/i.test(content));
    });

    return issueCandidate?.index ?? candidates[0]?.index ?? 0;
};

const getReportIssueCandidates = (messages) => (messages || [])
    .filter((message) => (
        message?.role === 'user' &&
        cleanText(message?.content) &&
        !isReportCommand(message.content) &&
        !isReportChoiceText(message.content) &&
        !GENERIC_REPORT_REQUEST_PATTERN.test(cleanText(message.content)) &&
        !isGenericDiagnosticPrompt(cleanText(message.content))
    ))
    .map((message) => cleanText(message.content));

const getIssueFromTranscript = (messages) => {
    const candidates = getReportIssueCandidates(messages);

    // 1. Prefer a message that actually describes a fault/symptom — this is the real issue,
    //    even when the chat OPENED with a parts question or how-to (the reported bug: the symptom
    //    must never be the first message copied verbatim when a later turn reports the problem).
    const symptomCandidate = candidates.find((content) => SYMPTOM_SIGNAL_PATTERN.test(content));
    if (symptomCandidate) return symptomCandidate;

    // 2. Otherwise a vehicle-related message that is NOT a how-to / parts / service request.
    const issueCandidate = candidates.find((content) => {
        const wordCount = content.split(/\s+/).filter(Boolean).length;
        return VEHICLE_ISSUE_PATTERN.test(content) && wordCount >= 3 && !NON_DIAGNOSTIC_INTENT_PATTERN.test(content);
    });
    if (issueCandidate) return issueCandidate;

    // 3. Last resort — any vehicle-ish message, then the first substantive message.
    const looseCandidate = candidates.find((content) => {
        const wordCount = content.split(/\s+/).filter(Boolean).length;
        return VEHICLE_ISSUE_PATTERN.test(content) && wordCount >= 3;
    });
    return looseCandidate || candidates[0] || 'Vehicle diagnostic issue';
};

// True when ANY user turn describes a genuine fault/symptom — i.e. this is a real diagnostic
// conversation and not purely parts-shopping / how-to / service-logging.
export const conversationHasDiagnosticSignal = (messages = []) =>
    getReportIssueCandidates(messages).some((content) => SYMPTOM_SIGNAL_PATTERN.test(content));

// True when the conversation is clearly NON-diagnostic: no turn reports a symptom, and at least
// one substantive turn reads as a how-to, parts comparison/shopping, or service-logging request.
// Used so the client never fabricates a generic symptom report for such chats when the backend
// report call fails/times out (the backend already refuses them with not_diagnostic).
export const conversationLooksNonDiagnostic = (messages = []) => {
    if (conversationHasDiagnosticSignal(messages)) return false;
    const candidates = getReportIssueCandidates(messages);
    if (candidates.length === 0) return false;
    return candidates.some((content) => NON_DIAGNOSTIC_INTENT_PATTERN.test(content));
};

const getQuestionAnswers = (messages) => {
    const answers = [];
    let pendingQuestion = '';

    messages.forEach((message) => {
        if (message.role === 'assistant') {
            const question = extractQuestion(message.content);
            if (question && !REPORT_READY_PATTERN.test(cleanText(message.content))) {
                pendingQuestion = question;
            }
            return;
        }

        if (message.role !== 'user' || !pendingQuestion) return;

        const answer = cleanText(message.content);
        if (!answer || isReportCommand(answer)) return;

        answers.push({
            question: pendingQuestion,
            answer,
        });
        pendingQuestion = '';
    });

    return answers;
};

const createCause = ({
    vehicle,
    title,
    score,
    recommendedCheck,
    checkDescription,
    toolSummary,
    timeLabel,
    expectedResult,
    requiredTools = [],
    requiredParts = [],
    likelyRepair,
    diyRepairCost,
    repairTime,
    diyDifficulty,
    safetyPrecautions = [],
    guidedSteps = [],
    repairSteps = [],
    steps,
    whyItFits,
}) => {
    const confidenceScore = clampScore(score);
    return {
        id: slugify(title),
        title,
        confidenceScore,
        confidenceLabel: scoreToLabel(confidenceScore),
        recommendedCheck,
        checkDescription,
        toolSummary,
        timeLabel,
        expectedResult,
        requiredTools,
        requiredParts,
        likelyRepair,
        diyRepairCost,
        repairTime,
        diyDifficulty: normalizeDifficulty(diyDifficulty),
        safetyPrecautions: safetyPrecautions.length > 0
            ? safetyPrecautions
            : buildDefaultSafetyPrecautions({ title, requiredTools }),
        guidedSteps,
        repairSteps: buildDetailedRepairSteps({ repairSteps, guidedSteps, steps }),
        steps,
        whyItFits,
        forumUrl: createForumUrl(vehicle, title),
        youtubeUrl: createYoutubeUrl(vehicle, title),
    };
};

const buildNoStartSymptoms = (issue, details) => {
    const bullets = [];
    const answers = details.map((item) => cleanText(item.answer).toLowerCase());
    const combined = answers.join(' ');

    if (/\bslow crank|cranking slowly|slow to start\b/i.test(`${issue} ${combined}`)) {
        bullets.push('Engine cranks very slowly, struggling to turn over');
    }
    if (/\bcranks\b/i.test(combined) && !bullets.some((item) => item.includes('cranks very slowly'))) {
        bullets.push('Engine cranks when trying to start');
    }
    if (/\brapid clicking|repeated clicking|multiple clicks\b/i.test(combined)) {
        bullets.push('Rapid clicking is heard during the start attempt');
    }
    if (/\bsingle click|just clicking|still just clicking|one click\b/i.test(combined)) {
        bullets.push('A single solid click is heard during the start attempt');
    }
    if (/\byes[, ]*normal|normal warning lights|dashboard .* normally|lights stay bright|they stay bright\b/i.test(combined)) {
        bullets.push('Dashboard warning lights come on normally');
    }
    if (/\bheadlights go dim|lights dim|nearly out|almost out\b/i.test(combined)) {
        bullets.push('Headlights or dash lights dim during crank');
    }
    if (/\bmore than 2 years|more than two years|3 years|4 years|5 years|old battery|never replaced\b/i.test(combined)) {
        bullets.push('Battery has likely aged beyond its best service life');
    }
    if (/\bhappened suddenly|all of a sudden|suddenly\b/i.test(combined)) {
        bullets.push('Problem happened suddenly rather than gradually');
    }

    if (bullets.length === 0) {
        bullets.push(createSymptomBullet(issue));
    }

    return bullets;
};

const buildNoStartCauses = (vehicle, issue, details) => {
    const issueText = cleanText(issue).toLowerCase();
    const answerText = toAnswerText(details);
    const combined = `${issueText} ${answerText}`;

    const flags = {
        rapidClicking: /\brapid clicking|repeated clicking|multiple clicks|machine-gun/i.test(combined),
        singleClick: /\bsingle click|just clicking|still just clicking|one click/i.test(combined),
        silent: /\bcompletely silent|nothing at all|no sound|silent\b/i.test(combined),
        cranks: /\byes[, ]*it cranks|cranks|turns over/i.test(combined),
        slowCrank: /\bcranking slowly|slow to start|slow crank/i.test(combined),
        dimLights: /\bheadlights go dim|lights dim|nearly out|almost out\b/i.test(combined),
        lightsNormal: /\byes[, ]*normal|normal warning lights|dashboard .* normally|lights stay bright|they stay bright\b/i.test(combined),
        batteryOld: /\bmore than 2 years|more than two years|3 years|4 years|5 years|old battery|never replaced\b/i.test(combined),
        sudden: /\bhappened suddenly|all of a sudden|suddenly\b/i.test(combined),
        neutralHelps: /\bstarts in neutral|neutral\b/i.test(combined),
    };

    const causes = [
        createCause({
            vehicle,
            title: 'Weak or failed battery',
            score: 76
                + (flags.rapidClicking ? 10 : 0)
                + (flags.dimLights ? 10 : 0)
                + (flags.batteryOld ? 8 : 0)
                + (flags.slowCrank ? 7 : 0)
                - (flags.lightsNormal && !flags.rapidClicking ? 6 : 0),
            recommendedCheck: 'Test Battery Voltage',
            checkDescription: 'Confirm or rule out the battery before replacing parts.',
            toolSummary: 'Digital Multimeter',
            timeLabel: '2-5 min',
            expectedResult: '12.6V+ at rest — below 12.2V means the battery is weak or discharged',
            requiredTools: [
                createTool('Digital Multimeter', 'From $18.99'),
            ],
            requiredParts: [
                createPart('Replacement battery', 'Match OEM group size and cold cranking amps'),
                createPart('Terminal protector spray', 'Recommended after installation'),
            ],
            likelyRepair: 'Battery Replacement',
            diyRepairCost: '$120-$200',
            repairTime: '30-60 min',
            diyDifficulty: 'Easy',
            safetyPrecautions: [
                'Disconnect the negative battery terminal before removing or installing the battery.',
                'Wear gloves and eye protection because batteries can vent gas or leak acid residue.',
                'Do not let a metal tool bridge both battery terminals at the same time.',
            ],
            guidedSteps: [
                createGuidedStep('Park the vehicle on a flat surface, turn the ignition completely off, and wait 2 minutes before testing.', '', 'Digital multimeter'),
                createGuidedStep('Open the hood and locate the battery. Identify the positive (+) and negative (-) terminals.', '', 'Digital multimeter'),
                createGuidedStep('Set your digital multimeter to DC voltage — select the 20V range.', '', 'Digital multimeter'),
                createGuidedStep('Touch the RED probe to the positive (+) battery terminal.', 'The positive terminal is usually marked with a + symbol and often has a red plastic cover.', 'Digital multimeter'),
                createGuidedStep('Touch the BLACK probe to the negative (–) battery terminal.', '', 'Digital multimeter'),
                createGuidedStep('Read the voltage shown on the multimeter display. Note your exact measurement.', '12.6V+ = fully charged. 12.2–12.5V = partially discharged. Below 12.2V = weak or dead battery.', 'Digital multimeter'),
            ],
            steps: [
                'Measure battery voltage with the engine off.',
                'If voltage is low, charge the battery fully or try a known-good jump pack.',
                'If the car starts after charging or jumping, test the battery and alternator before replacing anything else.',
            ],
            repairSteps: [
                { instruction: 'Disconnect the negative (–) terminal first', detail: 'Turn the ignition fully off, then loosen and remove the black negative cable before anything else — this prevents accidental short circuits and sparks.' },
                { instruction: 'Disconnect the positive (+) terminal and hold-down', detail: 'Remove the red positive cable, then unbolt the bracket clamping the battery down so it can lift out.' },
                { instruction: 'Lift out the old battery and clean the tray', detail: 'Batteries are heavy — lift straight up with both hands. Wipe away any white or green residue so the new battery sits on a clean, dry tray.' },
                { instruction: 'Fit the new battery, reconnect positive first then negative', detail: 'Place it in the same orientation, secure the hold-down so it cannot move, then attach the red positive cable first and the black negative last. Snug, not over-tight.' },
                { instruction: 'Start the car and confirm it charges', detail: 'Spray terminal protector on the posts and start the engine. With it running, voltage should read about 13.8–14.4V and the car should start strongly — that confirms success.' },
            ],
            whyItFits: 'Battery age, slow cranking, and repeated clicking strongly suggest the battery cannot supply enough cold cranking amps for startup.',
        }),
        createCause({
            vehicle,
            title: 'Corroded or loose battery terminals',
            score: 68
                + (flags.rapidClicking ? 8 : 0)
                + (flags.singleClick ? 5 : 0)
                + (flags.sudden ? 5 : 0)
                + (flags.dimLights ? 4 : 0),
            recommendedCheck: 'Inspect Battery Terminals',
            checkDescription: 'Look for high-resistance connections at the battery and ground points.',
            toolSummary: 'Wrench, wire brush',
            timeLabel: '5-10 min',
            expectedResult: 'Any white or green corrosion, loose clamp, or hot cable suggests a poor battery connection',
            requiredTools: [
                createTool('10mm wrench or battery terminal tool'),
                createTool('Wire brush or terminal cleaner', 'From $7.99'),
            ],
            requiredParts: [
                createPart('Battery terminal cleaner', 'Or baking soda solution for corrosion cleanup'),
                createPart('Replacement terminal ends or shims', 'Only if the clamps are damaged or loose after cleaning'),
            ],
            likelyRepair: 'Terminal Cleaning / Cable Service',
            diyRepairCost: '$10-$40',
            repairTime: '20-45 min',
            diyDifficulty: 'Easy',
            safetyPrecautions: [
                'Disconnect the negative battery terminal before cleaning the battery posts.',
                'Wear gloves and eye protection when handling corrosion or cleaning chemicals.',
                'Dry the terminals completely before reconnecting the battery.',
            ],
            guidedSteps: [
                createGuidedStep('Turn the ignition fully off and inspect the positive battery terminal closely.', 'Look for white, green, or bluish buildup around the clamp and post.', 'Battery terminal tool'),
                createGuidedStep('Inspect the negative terminal and the main ground cable connection.', 'Check for corrosion, looseness, frayed strands, or a clamp that can be rotated by hand.', 'Battery terminal tool'),
                createGuidedStep('Gently wiggle each battery clamp and cable end.', 'A clamp should feel secure and should not shift on the battery post.', 'Battery terminal tool'),
                createGuidedStep('Check whether corrosion extends under the insulation or along the cable ends.', 'Hidden corrosion can create major resistance even if the top of the clamp looks acceptable.', 'Wire brush'),
                createGuidedStep('If safe to do so, clean visible corrosion and retighten the connections before retrying the start.', 'Use a proper battery cleaner or baking soda and water, then dry the terminals thoroughly.', 'Wire brush'),
            ],
            steps: [
                'Turn the vehicle fully off and remove the key.',
                'Inspect the positive and negative battery clamps plus the engine/body grounds.',
                'Clean corrosion, tighten the connections, and retry starting before moving deeper into the starter circuit.',
            ],
            repairSteps: [
                { instruction: 'Disconnect the negative terminal first', detail: 'Always remove the black negative cable before cleaning either post, so you cannot cause a spark or short while working.' },
                { instruction: 'Clean both posts and clamps to bright metal', detail: 'Remove both clamps and scrub the inside of the terminals and the battery posts with a wire brush until shiny metal shows — corrosion blocks the current the starter needs.' },
                { instruction: 'Inspect the cable ends for damage', detail: 'Check for broken strands, swelling, or heat marks on the cable ends. Replace a damaged end — a bad cable can act just like a dead battery.' },
                { instruction: 'Reconnect tightly and check the ground', detail: 'Reattach the clamps firmly (positive first, negative last) and confirm the ground cable to the body/engine is clean and secure.' },
                { instruction: 'Retry starting', detail: 'Start the car. A strong, immediate crank means the connection was the problem. If it still struggles, move on to the starter circuit.' },
            ],
            whyItFits: 'A poor high-current connection can mimic a dead battery and often causes clicking or intermittent no-start behavior.',
        }),
        createCause({
            vehicle,
            title: 'Faulty starter motor or starter relay',
            score: 66
                + (flags.singleClick ? 12 : 0)
                + (flags.lightsNormal ? 8 : 0)
                + (flags.sudden ? 5 : 0)
                - (flags.rapidClicking ? 4 : 0),
            recommendedCheck: 'Verify Starter Feed Voltage',
            checkDescription: 'Confirm whether the starter motor is receiving the crank command and battery power.',
            toolSummary: 'Multimeter or test light',
            timeLabel: '10-15 min',
            expectedResult: 'Full battery voltage at the starter during crank with no starter movement points to a failed starter or solenoid',
            requiredTools: [
                createTool('Digital Multimeter', 'From $18.99'),
                createTool('Test light'),
            ],
            requiredParts: [
                createPart('Starter motor / solenoid assembly', 'Verify engine and transmission fitment before ordering'),
                createPart('Starter relay', 'Replace if relay testing fails'),
            ],
            likelyRepair: 'Starter Motor / Relay Replacement',
            diyRepairCost: '$180-$450',
            repairTime: '60-120 min',
            diyDifficulty: 'Medium',
            safetyPrecautions: [
                'Disconnect the battery before touching the starter power cable or mounting bolts.',
                'Use jack stands if starter access requires working underneath the vehicle.',
                'Keep hands, tools, and wiring clear of moving components during live voltage checks.',
            ],
            guidedSteps: [
                createGuidedStep('Make sure the battery is charged and both battery connections are tight before testing the starter circuit.', '', 'Digital multimeter'),
                createGuidedStep('Locate the starter or starter relay and listen for a solid click when the key is turned to START.', 'A single click often means the control signal is reaching the circuit.', 'Test light'),
                createGuidedStep('Check for battery voltage at the starter solenoid control terminal during crank.', 'If there is no signal here, the issue may be upstream in the relay or switch.', 'Digital multimeter'),
                createGuidedStep('If full battery voltage reaches the starter but the motor does not turn, the starter or solenoid is likely faulty.', '', 'Digital multimeter'),
            ],
            steps: [
                'Verify the battery is charged and terminal connections are solid first.',
                'Check for starter relay operation and voltage at the starter solenoid when the key is turned.',
                'If voltage is present but the starter does not crank, the starter motor or solenoid is likely faulty.',
            ],
            repairSteps: [
                { instruction: 'Disconnect the battery', detail: 'Remove the negative terminal before touching the starter wiring so the thick power cable is not live while you work.' },
                { instruction: 'Raise and support the vehicle if needed', detail: 'Many starters are reached from underneath. Use ramps or jack stands on firm, level ground — never rely on the jack alone.' },
                { instruction: 'Label and remove the starter wires and bolts', detail: 'Note where the thick power wire and the small signal wire connect, disconnect them, then remove the starter mounting bolts.' },
                { instruction: 'Install the new starter or relay and reconnect wiring', detail: 'Bolt the replacement in, tighten the fasteners firmly, and reattach the wires exactly as they came off.' },
                { instruction: 'Reconnect the battery and test cranking', detail: 'Reconnect the battery and start the engine. A clean, strong crank with no clicking confirms the starter was the issue.' },
            ],
            whyItFits: 'A single click with bright lights often means the start command is reaching the starter circuit but the starter is not turning.',
        }),
        createCause({
            vehicle,
            title: 'Ignition switch or start circuit fault',
            score: 58
                + (flags.silent ? 14 : 0)
                + (flags.neutralHelps ? 9 : 0)
                + (flags.lightsNormal ? 4 : 0),
            recommendedCheck: 'Trace Start Command Circuit',
            checkDescription: 'Check whether the ignition switch and safety interlocks are sending a valid crank signal.',
            toolSummary: 'Test light or multimeter',
            timeLabel: '10-20 min',
            expectedResult: 'No crank signal at the relay or starter control side indicates a switch, relay, or interlock problem',
            requiredTools: [
                createTool('Digital Multimeter', 'From $18.99'),
                createTool('Vehicle wiring diagram'),
            ],
            requiredParts: [
                createPart('Starter relay or ignition switch', 'Replace only after confirming the failed section of the circuit'),
                createPart('Neutral safety / clutch switch', 'As needed if interlock testing fails'),
            ],
            likelyRepair: 'Switch / Relay / Interlock Repair',
            diyRepairCost: '$40-$220',
            repairTime: '30-90 min',
            diyDifficulty: 'Medium',
            safetyPrecautions: [
                'Disconnect the battery before removing trim panels or probing start-circuit connectors.',
                'Use the correct wiring diagram so you do not back-probe the wrong circuit.',
                'Release connector locks carefully and do not force ignition-switch or relay plugs.',
            ],
            guidedSteps: [
                createGuidedStep('Try starting in Neutral if the vehicle is automatic, or depress the clutch fully if it is manual.', '', 'Test light'),
                createGuidedStep('Check the starter fuse and starter relay first.', 'Swap the relay with a matching known-good relay only if your fuse box layout confirms it is safe.', 'Test light'),
                createGuidedStep('Verify the ignition switch is sending a start signal when the key is turned.', '', 'Digital multimeter'),
                createGuidedStep('If the start signal does not reach the relay or solenoid, inspect the neutral safety switch or clutch interlock circuit.', '', 'Digital multimeter'),
            ],
            steps: [
                'Try starting in Neutral if the vehicle is automatic.',
                'Check the ignition switch output, starter fuse, relay, and neutral safety or clutch switch.',
                'If there is no start signal, trace the control circuit before replacing the starter.',
            ],
            repairSteps: [
                { instruction: 'Confirm the failed part before replacing it', detail: 'Use a multimeter or test light to verify which control part (relay, ignition switch, or interlock) has actually failed — do not replace parts on a guess.' },
                { instruction: 'Access the relay or switch', detail: 'Remove the fuse-box cover or trim panels needed to reach the starter relay, ignition switch, or neutral-safety/clutch switch.' },
                { instruction: 'Replace the failed relay or switch', detail: 'Swap in the confirmed-bad component and make sure every connector clicks fully into place.' },
                { instruction: 'Recheck the crank signal', detail: 'Before reassembling trim, confirm the start signal now reaches the relay or starter control wire when the key is turned.' },
                { instruction: 'Verify consistent starting', detail: 'Make sure the engine starts reliably in Park and Neutral (automatic) or with the clutch fully pressed (manual).' },
            ],
            whyItFits: 'If the vehicle stays silent or behaves differently in Neutral, the problem may be in the start command circuit rather than the battery itself.',
        }),
    ];

    const ranked = causes
        .sort((a, b) => b.confidenceScore - a.confidenceScore)
        .map((cause, index) => ({
            ...cause,
            kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
        }));

    const includeFourthCause = ranked[3] && (
        ranked[3].confidenceScore >= 62 ||
        ranked[0].confidenceScore - ranked[3].confidenceScore <= 18
    );

    const visibleCauses = ranked.slice(0, includeFourthCause ? 4 : 3);

    return {
        severity: 'High Severity',
        safeToDrive: 'No',
        safetyNote: 'Vehicle is currently immobile. Avoid repeated starting attempts because excess heat can damage the starter and battery cables.',
        canDriveMessage: 'Vehicle is currently immobile. Do not attempt repeated starting as this can damage the starter motor. Diagnose before continuing.',
        canDriveTimeframe: 'Address within 1-2 days.',
        summary: `${vehicle} has a no-start condition. The answers point first to battery power delivery, then to the starter circuit. Confirm the top-ranked causes in order before replacing parts.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: buildNoStartSymptoms(issue, details),
        likelyCauses: visibleCauses,
        recommendedChecks: visibleCauses.map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}. Top suspects are ${visibleCauses.map((cause) => cause.title.toLowerCase()).join(', ')}. Start with the highest-confidence electrical checks before replacing components.`,
    };
};

const buildGeneralAssessment = (vehicle, issue, details) => {
    const answerText = toAnswerText(details);
    const likelyCauses = [
        createCause({
            vehicle,
            title: 'Primary component fault related to the reported symptom',
            score: 78,
            recommendedCheck: 'Verify Primary Fault',
            checkDescription: 'Inspect the component most directly tied to the symptom and confirm the failure before replacing parts.',
            toolSummary: 'Flashlight, scan tool',
            timeLabel: '10-20 min',
            expectedResult: 'Visible fault, repeatable symptom, or matching trouble code confirms the primary component path',
            requiredTools: [
                createTool('Flashlight'),
                createTool('OBD-II scan tool'),
            ],
            likelyRepair: 'Primary Component Repair',
            diyRepairCost: '$50-$250+',
            repairTime: '30-120 min',
            diyDifficulty: 'Medium',
            safetyPrecautions: [
                'Work only with the vehicle safely parked and the engine cooled if you are near hot components.',
                'Disconnect battery power before unplugging a primary electrical component.',
                'Take reference photos before removing hoses, clips, or connectors so reassembly is easier.',
            ],
            repairSteps: [
                { instruction: 'Park safely and get a flashlight', detail: 'Park on flat ground, switch off, and let hot parts cool. Grab a flashlight so you can see clearly into the engine bay or under the car.' },
                { instruction: 'Recreate the symptom safely', detail: 'Make the problem happen on purpose in a safe spot (idling in park, or a slow drive) so you know exactly when and how it shows up. This points you straight to the system at fault.' },
                { instruction: 'Take a "before" photo', detail: 'Snap a photo of the area before you touch anything. It makes putting hoses, clips, and connectors back easy and correct.' },
                { instruction: 'Inspect the main part involved', detail: 'Look closely at the component tied to the symptom for cracks, leaks, burn marks, looseness, or damage. Compare it to how a healthy part should look.' },
                { instruction: 'Check its connectors and linkages', detail: 'Wiggle the electrical plugs, hoses, and mechanical links attached to that part. Reseat anything loose and look for corrosion or splits.' },
                { instruction: 'Confirm the fault before replacing', detail: 'Note what you found and make sure it explains your symptom. Only replace the part once you\'re confident it\'s the cause — then retest to confirm the fix.' },
            ],
            guidedSteps: [
                createGuidedStep('Reproduce the symptom under safe conditions so you can confirm exactly what the vehicle is doing.', '', 'Flashlight'),
                createGuidedStep('Inspect the primary component related to the symptom for visible wear, leaks, looseness, or damage.', '', 'Flashlight'),
                createGuidedStep('Check related electrical connectors, hoses, or mechanical linkages attached to that component.', '', 'Basic hand tools'),
                createGuidedStep('Record what changed during the test before deciding whether the component is confirmed faulty.', '', 'Notepad'),
            ],
            steps: [
                'Reproduce the symptom under safe conditions.',
                'Inspect the directly related component for wear, leaks, heat damage, or mechanical binding.',
                'Confirm the fault before replacing parts.',
            ],
            whyItFits: 'The symptom is centered on one system, so the most direct mechanical or electrical fault is still the leading suspect.',
        }),
        createCause({
            vehicle,
            title: 'Electrical, fuse, relay, or sensor issue',
            score: answerText ? 70 : 66,
            recommendedCheck: 'Scan Codes And Power Supply',
            checkDescription: 'Check for stored faults and verify stable power, ground, fuse, and connector condition.',
            toolSummary: 'OBD-II scanner',
            timeLabel: '5-15 min',
            expectedResult: 'Active or pending codes plus circuit faults point to the affected electrical path',
            requiredTools: [
                createTool('OBD-II scan tool'),
                createTool('Digital Multimeter', 'From $18.99'),
            ],
            likelyRepair: 'Electrical Or Sensor Repair',
            diyRepairCost: '$20-$180+',
            repairTime: '20-90 min',
            diyDifficulty: 'Medium',
            safetyPrecautions: [
                'Switch the ignition off before unplugging sensors or removing fuses.',
                'Use insulated probes when checking live circuits.',
                'Do not replace modules or sensors until power, ground, and connector condition are confirmed.',
            ],
            repairSteps: [
                { instruction: 'Get a scan tool and a flashlight ready', detail: 'You\'ll need an OBD-II scan tool (many auto-parts stores lend them for free) and a good flashlight. Park on level ground and turn the ignition off to begin.' },
                { instruction: 'Plug in the scan tool and read the codes', detail: 'Find the OBD-II port under the dashboard near the steering column, plug in, turn the key to ON (engine off), and read any active or pending codes. Write down every code (like "P0301") — each one points to a specific circuit.' },
                { instruction: 'Check the fuse for that system', detail: 'Open the fuse box (the lid usually has a map), pull the fuse for the affected system, and look at the thin metal strip inside. A broken strip means it blew — replace it with the exact same amp rating.' },
                { instruction: 'Inspect the connectors and ground points', detail: 'Unplug the connectors near the affected part and look for green/white crust, bent pins, or a loose fit. Check that nearby ground straps (bare wires bolted to metal) are tight and clean — a loose ground causes random, on-and-off faults.' },
                { instruction: 'Clean and reseat anything suspect', detail: 'Spray light corrosion with electrical contact cleaner, push each connector back together until it clicks, and tighten any loose ground bolt. Do this before buying any sensor or module.' },
                { instruction: 'Clear the codes and retest', detail: 'Use the scan tool to clear the codes, then drive and recreate the situation. If the code and symptom stay gone, a wiring/fuse fault was the cause; if the same code returns, the named sensor or module likely needs replacing.' },
            ],
            guidedSteps: [
                createGuidedStep('Connect a scan tool and look for active or pending diagnostic trouble codes.', '', 'OBD-II scanner'),
                createGuidedStep('Inspect the fuse and relay path for the affected system.', '', 'Digital multimeter'),
                createGuidedStep('Check visible wiring, grounds, and connectors for looseness or corrosion.', '', 'Flashlight'),
                createGuidedStep('Retest the symptom after correcting any obvious circuit issue.', '', 'OBD-II scanner'),
            ],
            steps: [
                'Scan for active or pending diagnostic trouble codes.',
                'Inspect fuses, relays, wiring plugs, and grounds for the affected circuit.',
                'Repair any obvious electrical faults before replacing modules or major parts.',
            ],
            whyItFits: 'Intermittent or unclear symptoms often come from missing sensor data or unstable power delivery.',
        }),
        createCause({
            vehicle,
            title: 'Wear-related component failure',
            score: 64,
            recommendedCheck: 'Inspect Wear Item Condition',
            checkDescription: 'Compare mileage, age, and visible condition against the service life of the suspected wear item.',
            toolSummary: 'Basic hand tools',
            timeLabel: '10-25 min',
            expectedResult: 'Excessive wear, looseness, or damage confirms end-of-life failure',
            requiredTools: [
                createTool('Basic hand tools'),
            ],
            likelyRepair: 'Wear Item Replacement',
            diyRepairCost: '$30-$220+',
            repairTime: '30-120 min',
            diyDifficulty: 'Medium',
            safetyPrecautions: [
                'Support any moving or spring-loaded component before removing hardware.',
                'Wear gloves when handling sharp or heat-damaged parts.',
                'Compare the old part and new part carefully before installation.',
            ],
            repairSteps: [
                { instruction: 'Note your mileage and last service', detail: 'Write down the current mileage and when the suspected part was last replaced. Most wear parts have a rough service life, so this alone tells you if it\'s simply due.' },
                { instruction: 'Park safely and let hot parts cool', detail: 'Park on flat ground and switch off. If you\'ll need to go underneath, raise the car and set it on jack stands — never work under a vehicle held only by the jack.' },
                { instruction: 'Find and look closely at the suspected part', detail: 'Locate the wear item tied to your symptom and shine a light on it. Look for cracks, fraying, leaks, looseness, or shiny worn spots. Compare it to the matching part on the other side if there is one.' },
                { instruction: 'Wiggle-test it by hand', detail: 'Gently try to move the part. Noticeable play, clunking, or rough/gritty movement usually confirms it is worn out.' },
                { instruction: 'Confirm it actually causes your symptom', detail: 'Make sure the worn part matches the problem you have before buying anything — replacing a part just because it "looks old" wastes money if it isn\'t the real cause.' },
                { instruction: 'Replace the part and test again', detail: 'Fit the new part, matching the old one exactly, then take a short test drive and confirm the original symptom is gone.' },
            ],
            guidedSteps: [
                createGuidedStep('Review mileage and recent maintenance history for the suspected wear item.', '', 'Owner manual'),
                createGuidedStep('Inspect the part for looseness, cracking, contamination, or excessive wear.', '', 'Flashlight'),
                createGuidedStep('Compare the condition against normal service limits if available.', '', 'Service guide'),
                createGuidedStep('Confirm the worn part is actually causing the symptom before replacement.', '', 'Basic hand tools'),
            ],
            steps: [
                'Review recent maintenance and mileage.',
                'Inspect common wear items tied to the complaint.',
                'Confirm excessive wear or failure before replacing the part.',
            ],
            whyItFits: 'Mileage and age often turn a borderline part into a repeatable failure.',
        }),
        createCause({
            vehicle,
            title: 'Maintenance or fluid condition issue',
            score: 58,
            recommendedCheck: 'Check Fluids And Service Items',
            checkDescription: 'Rule out overdue maintenance or low/contaminated fluids before deeper teardown.',
            toolSummary: 'Dipstick, flashlight',
            timeLabel: '5-15 min',
            expectedResult: 'Low fluid, dirty filter, or overdue maintenance item can explain or worsen the symptom',
            requiredTools: [
                createTool('Flashlight'),
                createTool('Owner manual or service chart'),
            ],
            likelyRepair: 'Service / Fluid Correction',
            diyRepairCost: '$15-$120',
            repairTime: '15-45 min',
            diyDifficulty: 'Easy',
            safetyPrecautions: [
                'Let the engine cool before opening any cap or checking hot fluid systems.',
                'Use the fluid type recommended for your vehicle and avoid mixing unknown fluids.',
                'Wipe spills immediately and keep fluids off belts and electrical connectors.',
            ],
            repairSteps: [
                { instruction: 'Let the engine cool and park level', detail: 'Park on flat ground and wait until the engine is cool — checking some fluids hot can burn you and give a false reading.' },
                { instruction: 'Find the right dipstick or reservoir', detail: 'Use your owner\'s manual to locate the dipstick or reservoir for the fluid tied to your symptom (engine oil, coolant, brake, transmission, etc.). The caps are usually labelled or color-coded.' },
                { instruction: 'Check the level against the marks', detail: 'Pull the dipstick, wipe it, reinsert it, and pull again — the film should sit between the low and full marks. For reservoirs, read the MIN/MAX lines on the side.' },
                { instruction: 'Check the fluid\'s condition', detail: 'Look at the color and smell. Healthy oil is amber-to-brown; coolant is clear pink/green/orange. Milky, gritty, or burnt-smelling fluid is a warning sign worth noting.' },
                { instruction: 'Top up or correct the overdue item', detail: 'If a fluid is low, add the exact type your manual specifies, a little at a time, rechecking the level. Replace an obviously clogged filter if it\'s the easy item tied to the symptom.' },
                { instruction: 'Retest and watch the level', detail: 'Drive briefly, then recheck. If the symptom improves, the maintenance item was the cause. If a fluid drops again quickly, you likely have a leak that needs a closer look.' },
            ],
            guidedSteps: [
                createGuidedStep('Check the fluid level and fluid condition related to the reported issue.', '', 'Owner manual'),
                createGuidedStep('Inspect filters, caps, and serviceable wear items for obvious neglect.', '', 'Flashlight'),
                createGuidedStep('Correct any low-fluid or overdue service item that can safely be addressed first.', '', 'Basic hand tools'),
                createGuidedStep('Retest the symptom after the maintenance correction.', '', 'Notepad'),
            ],
            steps: [
                'Check fluid levels and visible condition.',
                'Inspect filters, belts, and overdue maintenance items.',
                'Correct low fluids or neglected service before deeper teardown.',
            ],
            whyItFits: 'An overdue maintenance item can create a misleading symptom chain even when no major component has failed.',
        }),
    ].map((cause, index) => ({
        ...cause,
        kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
    }));

    return {
        severity: 'Medium Severity',
        safeToDrive: 'Use caution',
        safetyNote: 'If warning lights, abnormal noises, smoke, fluid leaks, or loss of control are present, stop driving and inspect the vehicle before continuing.',
        canDriveMessage: 'Drive only if the symptom is mild and the vehicle remains controllable. Stop immediately if the issue worsens.',
        canDriveTimeframe: 'Inspect soon.',
        summary: `${vehicle} has a reported issue: ${issue}. Use the ranked checks below to confirm the root cause before replacing parts.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(issue), ...details.map((item) => createSymptomBullet(item.answer))].slice(0, 4),
        likelyCauses,
        recommendedChecks: likelyCauses.map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}`,
    };
};

const buildAcCoolingAssessment = (vehicle, issue, details) => {
    const answerText = toAnswerText(details);
    const weakAtIdle = /\bweak at idle|idle\b/i.test(answerText);
    const coldDrivingOnly = /\bcold while driving|driving only|better while moving\b/i.test(answerText);
    const notColdAtAll = /\bnot cold at all|not cold\b/i.test(answerText);

    const likelyCauses = [
        createCause({
            vehicle,
            title: 'Stuck temperature blend door or failed blend door actuator',
            score: 78 + (notColdAtAll ? 6 : 0),
            recommendedCheck: 'Verify Blend Door Movement',
            checkDescription: 'Confirm the HVAC blend door moves correctly when temperature is changed from full cold to hot.',
            toolSummary: 'Flashlight, trim tool',
            timeLabel: '10-20 min',
            expectedResult: 'No movement, clicking, or temperature staying warm points to a stuck blend door or bad actuator',
            requiredTools: [createTool('Flashlight'), createTool('Trim tool')],
            likelyRepair: 'Blend Door / Actuator Repair',
            diyRepairCost: '$40-$220+',
            repairTime: '30-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Set the HVAC to full cold, then slowly move it to full hot while listening behind the dash.', '', 'Flashlight'),
                createGuidedStep('Check whether airflow temperature changes smoothly or stays warm regardless of the setting.', '', 'Flashlight'),
                createGuidedStep('Listen for repeated clicking from the dash that suggests stripped actuator gears.', '', 'Flashlight'),
                createGuidedStep('Inspect the accessible actuator linkage or blend door arm if your vehicle layout allows it.', '', 'Trim tool'),
            ],
            repairSteps: [
                { instruction: 'Disconnect battery power', detail: 'Before unplugging any HVAC actuator or removing trim, disconnect the negative battery terminal to avoid blown fuses or shorts.' },
                { instruction: 'Access the blend-door actuator', detail: 'Remove the trim panel or glovebox section needed to reach the small motor (actuator) that moves the temperature-blend door.' },
                { instruction: 'Unplug and remove the old actuator', detail: 'Unplug it and take out its screws. Note the position of its arm so the new one goes in the same way (its “clocking”).' },
                { instruction: 'Install the new actuator and test temperature', detail: 'Fit the replacement, then cycle the temperature from cold to hot and confirm the air changes smoothly instead of staying warm.' },
                { instruction: 'Reassemble and verify cold air', detail: 'Refit the trim and check you get cold air both at idle and while driving — that confirms the door is now moving correctly.' },
            ],
            whyItFits: 'If the air stays warm or only changes slightly, the HVAC door that mixes hot and cold air may not be moving correctly.',
        }),
        createCause({
            vehicle,
            title: 'Low refrigerant charge or small refrigerant leak',
            score: 74 + (weakAtIdle ? 6 : 0) + (coldDrivingOnly ? 5 : 0),
            recommendedCheck: 'Inspect Refrigerant Performance',
            checkDescription: 'Check for compressor cycling, line temperature difference, and obvious leak signs before recharging.',
            toolSummary: 'A/C gauge set, flashlight',
            timeLabel: '10-15 min',
            expectedResult: 'Short cycling, weak cooling, or oily residue at A/C fittings suggests low refrigerant',
            requiredTools: [createTool('Flashlight'), createTool('A/C gauge set')],
            likelyRepair: 'Leak Repair / Refrigerant Service',
            diyRepairCost: '$40-$180+',
            repairTime: '30-90 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Start the engine, switch A/C to max, and observe whether the compressor clutch engages.', '', 'Flashlight'),
                createGuidedStep('Feel the A/C lines carefully — one should become noticeably colder than the other.', '', 'Flashlight'),
                createGuidedStep('Inspect hose fittings and condenser area for oily residue that suggests refrigerant leakage.', '', 'Flashlight'),
                createGuidedStep('Note whether cooling improves while driving and weakens at idle.', '', 'Notepad'),
            ],
            repairSteps: [
                { instruction: 'Find the leak before adding refrigerant', detail: 'Low refrigerant always means a leak somewhere. Look for oily residue at fittings or use UV dye — repeatedly topping up without fixing the leak wastes money.' },
                { instruction: 'Repair the leak source', detail: 'Replace the leaking O-rings, a damaged service-port valve core, or the failed component you found. The system must be sealed before recharging.' },
                { instruction: 'Evacuate (vacuum) the system', detail: 'Use a vacuum pump to pull out air and moisture for ~30 minutes. Skipping this lets moisture damage the system and ruins cooling.' },
                { instruction: 'Recharge with the correct amount', detail: 'Add the exact refrigerant charge listed on the under-hood A/C label for your vehicle — overcharging is as harmful as undercharging.' },
                { instruction: 'Verify vent temperature and pressures', detail: 'With A/C on max, the vents should blow cold (around 4–10°C/40–50°F) and the gauge pressures should sit in the normal range. That confirms the fix.' },
            ],
            whyItFits: 'Weak cooling, especially at idle or in hot weather, commonly points to low refrigerant or a small system leak.',
        }),
        createCause({
            vehicle,
            title: 'A/C compressor clutch or compressor control issue',
            score: 69 + (notColdAtAll ? 5 : 0),
            recommendedCheck: 'Verify Compressor Operation',
            checkDescription: 'Confirm the compressor clutch engages and stays engaged when the A/C is commanded on.',
            toolSummary: 'Flashlight, multimeter',
            timeLabel: '10-20 min',
            expectedResult: 'No clutch engagement or unstable cycling points to clutch, relay, pressure switch, or compressor control fault',
            requiredTools: [createTool('Flashlight'), createTool('Digital Multimeter', 'From $18.99')],
            likelyRepair: 'Compressor / Clutch Circuit Repair',
            diyRepairCost: '$50-$320+',
            repairTime: '30-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Turn the A/C on and watch whether the compressor clutch face pulls in.', '', 'Flashlight'),
                createGuidedStep('Check the A/C fuse and compressor relay if the clutch never engages.', '', 'Digital Multimeter'),
                createGuidedStep('Verify clutch power and ground when the system commands A/C on.', '', 'Digital Multimeter'),
                createGuidedStep('If power is present but the clutch does not engage, the clutch or compressor is likely faulty.', '', 'Digital Multimeter'),
            ],
            repairSteps: [
                { instruction: 'Disconnect battery power', detail: 'Remove the negative terminal before working on compressor wiring or relays so nothing is live.' },
                { instruction: 'Test the cheap parts first', detail: 'Check the A/C fuse, compressor relay, pressure switch, and clutch connector before condemning the compressor — these are far cheaper and often the real cause.' },
                { instruction: 'Replace the confirmed-bad part', detail: 'Replace whatever your testing proved faulty — the clutch-control component or, if needed, the compressor itself.' },
                { instruction: 'Recheck clutch engagement', detail: 'Turn A/C on and confirm the compressor clutch now pulls in and stays engaged.' },
                { instruction: 'Verify cold air at the vents', detail: 'Confirm the vents blow cold steadily — that means the compressor is now running properly.' },
            ],
            whyItFits: 'If the system never gets cold, the compressor may not be running long enough or at all.',
        }),
        createCause({
            vehicle,
            title: 'Cooling fan or condenser airflow problem',
            score: 64 + (weakAtIdle ? 9 : 0) + (coldDrivingOnly ? 7 : 0),
            recommendedCheck: 'Check Fan And Condenser Airflow',
            checkDescription: 'Inspect condenser airflow and cooling fan performance, especially if A/C is weaker at idle.',
            toolSummary: 'Flashlight',
            timeLabel: '5-10 min',
            expectedResult: 'Weak fan operation or blocked condenser fins often causes warm A/C at stops and better cooling while moving',
            requiredTools: [createTool('Flashlight')],
            likelyRepair: 'Cooling Fan / Airflow Repair',
            diyRepairCost: '$20-$250+',
            repairTime: '20-90 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('With the A/C on, confirm the radiator or condenser fan turns on and stays running.', '', 'Flashlight'),
                createGuidedStep('Inspect the condenser for dirt, leaves, plastic bags, or bent fins blocking airflow.', '', 'Flashlight'),
                createGuidedStep('Compare cooling at idle versus while driving at road speed.', '', 'Notepad'),
                createGuidedStep('If the fan is weak or intermittent, inspect the fan relay, fuse, and motor connector.', '', 'Flashlight'),
            ],
            repairSteps: [
                { instruction: 'Clear debris from the condenser', detail: 'Remove leaves, bugs, and dirt from the front of the condenser (the radiator-like part in front of the radiator) and gently straighten only lightly bent fins so air can flow through.' },
                { instruction: 'Test the cooling fan circuit', detail: 'Check the fan fuse, relay, and motor connector. With A/C on, the fan should run — if it does not, one of these is usually why.' },
                { instruction: 'Replace the failed fan part', detail: 'If airflow is still weak, replace the failed fan motor or control part confirmed by your testing.' },
                { instruction: 'Retest cooling at idle', detail: 'Sit with the engine idling and A/C on max — cold air that now holds up at a standstill confirms airflow was the problem.' },
            ],
            whyItFits: 'A/C that improves while driving but struggles at idle often lacks enough condenser airflow.',
        }),
    ].sort((a, b) => b.confidenceScore - a.confidenceScore).map((cause, index) => ({
        ...cause,
        kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
    }));

    return {
        severity: 'Medium Severity',
        safeToDrive: 'Use caution',
        safetyNote: 'A/C issues usually do not make the vehicle unsafe to drive, but stop if the engine is overheating or belt noise appears.',
        canDriveMessage: 'Usually safe to drive unless the engine temperature rises or the drive belt system is affected.',
        canDriveTimeframe: 'Inspect soon.',
        summary: `${vehicle} has an A/C cooling complaint. Start with blend door, refrigerant performance, compressor operation, and airflow checks in that order.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(issue), ...details.map((item) => createSymptomBullet(item.answer))].slice(0, 4),
        likelyCauses: likelyCauses.slice(0, 4),
        recommendedChecks: likelyCauses.slice(0, 4).map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}. Verify HVAC blend door response, refrigerant performance, compressor operation, and condenser airflow before replacing major A/C components.`,
    };
};

const buildDoorLockAssessment = (vehicle, issue, details) => {
    const answerText = toAnswerText(details);
    const noSound = /\bno sound|no click|nothing\b/i.test(answerText);
    const insideNormal = /\binside handle normal|both inside and outside|outside only\b/i.test(answerText);
    const windowWorks = /\bwindow works|speaker works\b/i.test(answerText);

    const likelyCauses = [
        createCause({
            vehicle,
            title: 'Failed door lock actuator inside the affected door',
            score: 80 + (noSound ? 6 : 0) + (windowWorks ? 4 : 0),
            recommendedCheck: 'Verify Lock Actuator Movement',
            checkDescription: 'Confirm whether the lock actuator receives a command and moves the lock mechanism.',
            toolSummary: 'Flashlight, trim tool',
            timeLabel: '10-20 min',
            expectedResult: 'No lock movement or no actuator sound during lock/unlock strongly points to a failed actuator',
            requiredTools: [createTool('Flashlight'), createTool('Trim tool')],
            likelyRepair: 'Door Lock Actuator Replacement',
            diyRepairCost: '$40-$180+',
            repairTime: '45-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Press lock and unlock while watching the interior lock knob closely.', '', 'Flashlight'),
                createGuidedStep('Listen for actuator sound from inside the affected door.', '', 'Flashlight'),
                createGuidedStep('Try the inside and outside handle after unlocking to see if the latch releases at all.', '', 'Flashlight'),
                createGuidedStep('If the knob never moves and no sound comes from the door, the actuator is the top suspect.', '', 'Flashlight'),
            ],
            repairSteps: [
                { instruction: 'Disconnect the battery', detail: 'Before unplugging any door electronics, disconnect the negative battery terminal to protect the circuits.' },
                { instruction: 'Remove the inner door panel', detail: 'Carefully unclip and lift off the inner trim panel, then peel back the plastic water shield behind it to reach the lock parts.' },
                { instruction: 'Remove the old actuator', detail: 'Disconnect the actuator’s rod linkage and electrical plug, then unbolt and take out the failed actuator (the small lock motor).' },
                { instruction: 'Install the new actuator and test it', detail: 'Fit the replacement, reconnect the rods and plug, then press lock/unlock and confirm it now works before closing up.' },
                { instruction: 'Reassemble and confirm both sides open', detail: 'Refit the water shield and trim panel, then check the door opens normally from the inside and outside handles.' },
            ],
            whyItFits: 'A door that stays locked with no actuator movement or sound usually has an internal actuator failure.',
        }),
        createCause({
            vehicle,
            title: 'Jammed latch or broken internal latch mechanism',
            score: 72 + (insideNormal ? 4 : 0),
            recommendedCheck: 'Check Latch Release Response',
            checkDescription: 'Determine whether the latch is jammed even when the lock command appears to work.',
            toolSummary: 'Flashlight',
            timeLabel: '10-15 min',
            expectedResult: 'Lock movement with no actual latch release points more to a jammed latch than a dead actuator',
            requiredTools: [createTool('Flashlight')],
            likelyRepair: 'Latch Mechanism Repair',
            diyRepairCost: '$30-$160+',
            repairTime: '45-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Unlock the door and pull the inside handle several times to feel whether the latch moves normally.', '', 'Flashlight'),
                createGuidedStep('Check whether the outside handle has normal resistance or feels disconnected.', '', 'Flashlight'),
                createGuidedStep('If the lock knob moves but the latch never releases, suspect a jammed latch or broken internal linkage.', '', 'Flashlight'),
            ],
            repairSteps: [
                { instruction: 'Open the door panel and inspect the latch', detail: 'Remove the inner trim and look at the latch assembly and the rods that connect it to the handles.' },
                { instruction: 'Lubricate and test the latch', detail: 'Spray the latch with white lithium grease and work it by hand to see whether it is just seized (frees up) or physically broken (stays stuck).' },
                { instruction: 'Replace the latch if it has failed', detail: 'If the release lever or internal mechanism is broken, fit a new latch assembly — lubrication alone will not fix a cracked part.' },
                { instruction: 'Reassemble and test both handles', detail: 'Put the panel back and confirm the door opens and locks correctly from both the inside and outside handles.' },
            ],
            whyItFits: 'If the lock command works but the door still will not release, the latch mechanism itself may be jammed.',
        }),
        createCause({
            vehicle,
            title: 'Broken handle linkage or disconnected door-handle rod',
            score: 67,
            recommendedCheck: 'Inspect Handle Linkage',
            checkDescription: 'Check whether the inside or outside handle is still connected to the latch.',
            toolSummary: 'Flashlight, trim tool',
            timeLabel: '10-20 min',
            expectedResult: 'A loose handle or no resistance often means the linkage rod or clip has come loose',
            requiredTools: [createTool('Flashlight'), createTool('Trim tool')],
            likelyRepair: 'Handle / Linkage Repair',
            diyRepairCost: '$15-$120+',
            repairTime: '30-90 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Pull the inside and outside handle gently and compare the resistance.', '', 'Flashlight'),
                createGuidedStep('Note whether one handle feels loose, disconnected, or moves too freely.', '', 'Flashlight'),
                createGuidedStep('Inspect the linkage clips and rods once trim access is available.', '', 'Trim tool'),
            ],
            repairSteps: [
                { instruction: 'Open the door panel to reach the linkages', detail: 'Remove the inner trim so you can see the rods connecting the inside and outside handles to the latch.' },
                { instruction: 'Reconnect or replace the loose linkage', detail: 'Snap any popped-off rod clips back on, or replace broken plastic retainers — a disconnected rod makes the door act stuck even though the latch is fine.' },
                { instruction: 'Replace a cracked handle', detail: 'If the handle pivot or lever itself is cracked, fit a new handle so it can pull the rod properly.' },
                { instruction: 'Test both handles before closing up', detail: 'Confirm both the inside and outside handles release the latch, then reinstall the trim.' },
            ],
            whyItFits: 'A disconnected linkage can make the door seem locked even when the latch itself is still functional.',
        }),
        createCause({
            vehicle,
            title: 'Door-jamb wiring fault or no power reaching the actuator',
            score: 64 + (windowWorks ? -3 : 5),
            recommendedCheck: 'Verify Door Harness Power',
            checkDescription: 'Check whether the actuator circuit has power and ground through the door-jamb harness.',
            toolSummary: 'Multimeter',
            timeLabel: '15-25 min',
            expectedResult: 'No voltage pulse at the actuator during lock/unlock indicates wiring, switch signal, or BCM output fault',
            requiredTools: [createTool('Digital Multimeter', 'From $18.99')],
            likelyRepair: 'Door Harness / Circuit Repair',
            diyRepairCost: '$20-$180+',
            repairTime: '30-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Check whether the window switch and speaker on the same door also work normally.', '', 'Flashlight'),
                createGuidedStep('Inspect the rubber door-jamb boot for broken wires or prior repairs.', '', 'Flashlight'),
                createGuidedStep('Back-probe the actuator connector while commanding lock/unlock if you have a meter.', '', 'Digital Multimeter'),
            ],
            repairSteps: [
                { instruction: 'Inspect the door-jamb wiring boot', detail: 'Open the rubber boot between the door and body and look for broken or stretched wires — they flex every time the door opens and crack over time.' },
                { instruction: 'Repair the damaged wires', detail: 'Fix broken wires with solder-and-seal connectors (or equivalent), making each joint solid and weatherproof.' },
                { instruction: 'Confirm power reaches the actuator', detail: 'With a meter, verify voltage now pulses at the actuator during lock/unlock before spending money on control modules.' },
                { instruction: 'Retest the door lock', detail: 'Operate the lock several times and confirm it works reliably now that the wiring is repaired.' },
            ],
            whyItFits: 'If actuator power never reaches the door, the fault can be in the harness rather than the latch hardware itself.',
        }),
    ].sort((a, b) => b.confidenceScore - a.confidenceScore).map((cause, index) => ({
        ...cause,
        kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
    }));

    return {
        severity: 'Medium Severity',
        safeToDrive: 'Use caution',
        safetyNote: 'A stuck door can be a safety issue if it limits occupant exit. Avoid carrying passengers if the door cannot be opened reliably.',
        canDriveMessage: 'Drive only if another usable exit remains and the stuck door does not create a safety hazard.',
        canDriveTimeframe: 'Inspect soon.',
        summary: `${vehicle} has a door-opening or lock complaint. Start with actuator and latch checks before moving to wiring diagnosis.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(issue), ...details.map((item) => createSymptomBullet(item.answer))].slice(0, 4),
        likelyCauses: likelyCauses.slice(0, 4),
        recommendedChecks: likelyCauses.slice(0, 4).map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}. Confirm actuator movement, latch release, handle linkage, and door-harness power before replacing door modules.`,
    };
};

const buildWheelRotationAssessment = (vehicle, issue, details) => {
    const answerText = toAnswerText(details);
    const oneWheel = /\bone tyre stuck|one tire stuck|one wheel\b/i.test(answerText);
    const burningSmell = /\bburning smell|hot smell\b/i.test(answerText);
    const allWheels = /\ball wheels\b/i.test(answerText);

    const likelyCauses = [
        createCause({
            vehicle,
            title: oneWheel ? 'Seized brake caliper or locked brake hardware' : 'Parking brake or brake system binding',
            score: 79 + (burningSmell ? 8 : 0) + (oneWheel ? 6 : 0),
            recommendedCheck: 'Check For Brake Lockup',
            checkDescription: 'Confirm whether a brake caliper, parking brake, or shoe assembly is holding the wheel.',
            toolSummary: 'Flashlight, jack',
            timeLabel: '10-20 min',
            expectedResult: 'One wheel that stays locked, runs hot, or smells burnt strongly suggests brake binding',
            requiredTools: [createTool('Flashlight'), createTool('Jack and jack stands')],
            likelyRepair: 'Brake Binding Repair',
            diyRepairCost: '$40-$260+',
            repairTime: '45-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Check whether only one wheel is stuck or several wheels are resisting movement.', '', 'Flashlight'),
                createGuidedStep('Feel carefully for abnormal heat near the affected brake after a short roll attempt.', '', 'Flashlight'),
                createGuidedStep('Inspect the caliper, parking brake cable, or drum hardware for obvious binding.', '', 'Flashlight'),
            ],
            repairSteps: [
                { instruction: 'Lift and support the affected corner', detail: 'Use a jack and jack stands on firm, level ground before going near the brake — never work under a wheel held only by the jack.' },
                { instruction: 'Remove the wheel and inspect the brake', detail: 'Take the wheel off and check the caliper slide pins, pads, rotor, and brake hose for anything binding or stuck.' },
                { instruction: 'Free or repair the stuck part', detail: 'Release or repair the seized caliper, parking-brake hardware, or stuck drum component that is causing the bind.' },
                { instruction: 'Reassemble and confirm free rotation', detail: 'Put it back together, bleed the brakes if you opened the hydraulics, and confirm the wheel now spins freely by hand.' },
            ],
            whyItFits: 'A locked wheel with heat or burning smell often points to brake hardware that is not releasing.',
        }),
        createCause({
            vehicle,
            title: 'Failed wheel bearing, hub, or axle component',
            score: 71 + (oneWheel ? 6 : 0),
            recommendedCheck: 'Inspect Hub And Axle Movement',
            checkDescription: 'Confirm whether the wheel hub or axle is mechanically jammed.',
            toolSummary: 'Jack, pry bar',
            timeLabel: '15-25 min',
            expectedResult: 'Mechanical binding at the hub or axle can prevent the wheel from rotating even without brake drag',
            requiredTools: [createTool('Jack and jack stands'), createTool('Pry bar')],
            likelyRepair: 'Hub / Bearing / Axle Repair',
            diyRepairCost: '$80-$350+',
            repairTime: '60-180 min',
            diyDifficulty: 'Hard',
            guidedSteps: [
                createGuidedStep('Lift the affected wheel and try rotating it by hand.', '', 'Jack and jack stands'),
                createGuidedStep('Listen for grinding, metal contact, or severe resistance from the hub area.', '', 'Flashlight'),
                createGuidedStep('Inspect the axle, CV joint, and hub area for visible breakage or displacement.', '', 'Flashlight'),
            ],
            repairSteps: [
                { instruction: 'Raise and support the vehicle securely', detail: 'Lift the affected corner and set it on jack stands on level ground before working on the hub or axle.' },
                { instruction: 'Inspect the hub, bearing, and axle', detail: 'Check the wheel bearing, hub, axle shaft, and CV joints for seizure, grinding, or visible breakage.' },
                { instruction: 'Replace the failed component', detail: 'Fit a new hub, bearing, or axle part as confirmed by your inspection — this is a heavier job, so allow plenty of time.' },
                { instruction: 'Torque to spec and confirm free spin', detail: 'Tighten all axle and suspension fasteners to the specified torque, then confirm the wheel rotates freely by hand.' },
            ],
            whyItFits: 'A seized hub or failed axle component can physically stop the wheel from rotating.',
        }),
        createCause({
            vehicle,
            title: allWheels ? 'Transmission or driveline lockup' : 'Parking brake cable or release mechanism failure',
            score: allWheels ? 74 : 64,
            recommendedCheck: allWheels ? 'Check Driveline Lockup' : 'Inspect Parking Brake Release',
            checkDescription: allWheels ? 'Determine whether the driveline or transmission is preventing wheel rotation.' : 'Confirm the parking brake fully releases at the affected wheel.',
            toolSummary: 'Flashlight',
            timeLabel: '10-20 min',
            expectedResult: allWheels ? 'All wheels resisting movement can point to transmission or driveline lockup.' : 'A stuck parking brake cable can hold the wheel even with the lever released.',
            requiredTools: [createTool('Flashlight')],
            likelyRepair: allWheels ? 'Driveline / Transmission Diagnosis' : 'Parking Brake Repair',
            diyRepairCost: allWheels ? '$120-$500+' : '$25-$160+',
            repairTime: allWheels ? '60-180 min' : '30-90 min',
            diyDifficulty: allWheels ? 'Hard' : 'Medium',
            guidedSteps: [
                createGuidedStep(allWheels ? 'Confirm whether all driven wheels resist movement even in Neutral.' : 'Release and reapply the parking brake while observing cable return movement.', '', 'Flashlight'),
                createGuidedStep(allWheels ? 'Check for transmission or differential noises while trying to roll the vehicle.' : 'Inspect the parking brake cable and lever at the affected wheel for sticking.', '', 'Flashlight'),
                createGuidedStep(allWheels ? 'Inspect the driveline and axle components for obvious breakage or jammed movement.' : 'Verify the cable fully returns after the parking brake is released.', '', 'Flashlight'),
            ],
            repairSteps: allWheels ? [
                { instruction: 'Confirm Neutral and parking brake off', detail: 'Make sure the car is in Neutral with the parking brake fully released before assuming a driveline fault.' },
                { instruction: 'Inspect the driveline and transmission', detail: 'Check the driveshaft, differential, and transmission outputs for lockup or mechanical breakage.' },
                { instruction: 'Repair the failed driveline part', detail: 'Fix or replace the driveline/transmission component identified — internal transmission work usually needs a professional.' },
            ] : [
                { instruction: 'Inspect the parking-brake cable and lever', detail: 'Trace the parking-brake cable to the affected wheel and check whether the lever returns fully when released.' },
                { instruction: 'Free or replace the sticking cable', detail: 'Lubricate or replace the seized cable or hardware so the brake can release completely.' },
                { instruction: 'Confirm the wheel spins freely', detail: 'With the parking brake released, check the wheel now turns freely by hand.' },
            ],
            whyItFits: allWheels ? 'If multiple wheels will not rotate, the fault may be further upstream in the driveline.' : 'A stuck parking brake mechanism can hold one wheel even when the service brakes are normal.',
        }),
    ].sort((a, b) => b.confidenceScore - a.confidenceScore).map((cause, index) => ({
        ...cause,
        kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
    }));

    return {
        severity: 'High Severity',
        safeToDrive: 'No',
        safetyNote: 'Do not drive a vehicle with a locked or non-rotating wheel. Continued movement can overheat brakes or damage driveline components.',
        canDriveMessage: 'Vehicle is not safe to drive until the wheel rotation problem is confirmed and corrected.',
        canDriveTimeframe: 'Address immediately.',
        summary: `${vehicle} has a wheel-rotation complaint. Start with brake lockup and hub checks before moving to driveline diagnosis.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(issue), ...details.map((item) => createSymptomBullet(item.answer))].slice(0, 4),
        likelyCauses: likelyCauses.slice(0, 4),
        recommendedChecks: likelyCauses.slice(0, 4).map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}. Confirm brake binding, hub/axle movement, and driveline lockup before towing or replacing major parts.`,
    };
};

const buildBrakeAssessment = (vehicle, issue, details) => {
    const answerText = toAnswerText(details);

    const likelyCauses = [
        createCause({
            vehicle,
            title: 'Worn brake pads or heavily scored brake rotors',
            score: 81,
            recommendedCheck: 'Inspect Front Brake Wear',
            checkDescription: 'Look for thin pad material, rotor scoring, and metal-on-metal contact signs.',
            toolSummary: 'Flashlight',
            timeLabel: '5-15 min',
            expectedResult: 'Very thin pads or deep rotor scoring confirm a brake wear problem',
            requiredTools: [createTool('Flashlight')],
            likelyRepair: 'Brake Pad / Rotor Replacement',
            diyRepairCost: '$90-$320+',
            repairTime: '60-150 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Look through the wheel spokes and inspect the pad thickness if visible.', '', 'Flashlight'),
                createGuidedStep('Check the rotor face for heavy grooves, scoring, or discoloration.', '', 'Flashlight'),
                createGuidedStep('Note whether the noise happens mainly while braking and from one side or both.', '', 'Notepad'),
            ],
            repairSteps: [
                { instruction: 'Lift the vehicle and remove the wheel', detail: 'Support it safely on jack stands, then take off the wheel at the brake making the noise.' },
                { instruction: 'Inspect the pads and rotor', detail: 'Check pad thickness and rotor condition. Always replace pads as a set across the axle (both sides) so braking stays even.' },
                { instruction: 'Replace pads and service the rotor', detail: 'Fit new pads, and machine or replace the rotor if it is below minimum thickness or heavily scored.' },
                { instruction: 'Torque, pump the pedal, then road-test', detail: 'Tighten hardware to spec, pump the brake pedal until firm BEFORE driving, then road-test gently — braking should be quiet and smooth.' },
            ],
            whyItFits: 'Grinding or harsh brake noise is most often caused by severely worn pads or damaged rotors.',
        }),
        createCause({
            vehicle,
            title: 'Stuck brake caliper or seized slide hardware',
            score: 71 + (/\bpulls|hot wheel|burning\b/i.test(answerText) ? 6 : 0),
            recommendedCheck: 'Check Caliper Release',
            checkDescription: 'Inspect whether the caliper or slide hardware is holding the brake on.',
            toolSummary: 'Flashlight, jack',
            timeLabel: '10-20 min',
            expectedResult: 'Uneven wear or one hot wheel points to a caliper or slide issue',
            requiredTools: [createTool('Flashlight'), createTool('Jack and jack stands')],
            likelyRepair: 'Caliper / Slide Service',
            diyRepairCost: '$40-$220+',
            repairTime: '45-120 min',
            diyDifficulty: 'Medium',
            guidedSteps: [
                createGuidedStep('Compare brake heat and smell side to side after a short drive if safe.', '', 'Flashlight'),
                createGuidedStep('Inspect pad wear on both sides of the same rotor for uneven wear.', '', 'Flashlight'),
                createGuidedStep('Check whether the caliper slides move freely once access is available.', '', 'Basic hand tools'),
            ],
            repairSteps: [
                { instruction: 'Lift the vehicle and remove the wheel', detail: 'Support it on jack stands, remove the wheel, and inspect the caliper at the affected brake.' },
                { instruction: 'Clean and lubricate the slide pins', detail: 'If the caliper slide pins are dry or sticking, clean them and apply proper brake grease so the caliper can release fully.' },
                { instruction: 'Replace the caliper or hose if needed', detail: 'If the piston is seized or the rubber hose has collapsed internally and is causing drag, replace that part.' },
                { instruction: 'Bleed (if opened) and road-test', detail: 'Bleed the brakes if you opened any hydraulic part, then road-test — the wheel should no longer drag, pull, or overheat.' },
            ],
            whyItFits: 'A sticking caliper can create grinding, pull, overheating, or rapid uneven pad wear.',
        }),
        createCause({
            vehicle,
            title: 'Brake dust shield or debris contacting the rotor',
            score: 63,
            recommendedCheck: 'Inspect Rotor Contact Points',
            checkDescription: 'Look for metal shield contact or trapped debris rubbing the rotor.',
            toolSummary: 'Flashlight',
            timeLabel: '5-10 min',
            expectedResult: 'A bent shield or trapped stone can cause scraping without major brake failure',
            requiredTools: [createTool('Flashlight')],
            likelyRepair: 'Shield / Debris Correction',
            diyRepairCost: '$0-$30',
            repairTime: '10-30 min',
            diyDifficulty: 'Easy',
            guidedSteps: [
                createGuidedStep('Inspect the rotor backing plate for contact marks or bending.', '', 'Flashlight'),
                createGuidedStep('Check for stones or debris trapped between shield and rotor.', '', 'Flashlight'),
                createGuidedStep('Gently move the shield away from the rotor if it is rubbing.', '', 'Basic hand tools'),
            ],
            repairSteps: [
                { instruction: 'Remove the wheel for access if needed', detail: 'Take the wheel off if you cannot reach the rubbing area through the spokes.' },
                { instruction: 'Clear debris and reshape the shield', detail: 'Remove any trapped stones and gently bend the thin metal dust shield away from the rotor so it stops scraping.' },
                { instruction: 'Spin the rotor to confirm it is quiet', detail: 'Turn the rotor by hand and confirm the scraping noise is gone before refitting the wheel.' },
            ],
            whyItFits: 'Not every brake noise is pad failure — shields and debris can also scrape the rotor.',
        }),
    ].sort((a, b) => b.confidenceScore - a.confidenceScore).map((cause, index) => ({
        ...cause,
        kindLabel: index === 0 ? 'Most Likely Cause' : 'Possible Cause',
    }));

    return {
        severity: 'High Severity',
        safeToDrive: 'Use caution',
        safetyNote: 'Brake noise or grinding can become unsafe quickly. Stop driving if braking distance increases, the pedal sinks, or the vehicle pulls hard.',
        canDriveMessage: 'Drive only if braking remains strong and controlled, and inspect immediately.',
        canDriveTimeframe: 'Inspect immediately.',
        summary: `${vehicle} has a brake-related complaint. Inspect brake wear, caliper release, and rotor contact points before continuing to drive.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(issue), ...details.map((item) => createSymptomBullet(item.answer))].slice(0, 4),
        likelyCauses: likelyCauses.slice(0, 4),
        recommendedChecks: likelyCauses.slice(0, 4).map((cause) => cause.recommendedCheck),
        mechanicSummary: `${vehicle}: ${issue}. Confirm pad and rotor wear first, then rule out caliper drag and shield or debris contact.`,
    };
};

const classifyDiagnosticCategory = (issue, details) => {
    const combined = `${cleanText(issue)} ${details.map((item) => cleanText(item.answer)).join(' ')}`.toLowerCase();
    if (/\b(no start|won'?t start|will not start|crank|click|battery|starter|not starting|not start)\b/i.test(combined)) return 'no_start';
    if (/\b(ac|a\/c|air conditioning|not cooling|cooling|compressor|refrigerant|cold air|blend door)\b/i.test(combined)) return 'ac_cooling';
    if (/\b(door|lock|unlock|latch|actuator|window works|speaker works|inside handle|outside handle)\b/i.test(combined)) return 'door_lock';
    if (/\b(tyre|tire|wheel|rotating|rotate|stuck wheel|burning smell|parking brake)\b/i.test(combined)) return 'wheel_rotation';
    if (/\b(brake|grinding|rotor|pad|caliper|braking)\b/i.test(combined)) return 'brake';
    return 'general';
};

export const createDiagnosticReport = ({ messages, reportMessage, vehicleLabel }) => {
    const transcript = messages || [];
    const diagnosticStartIndex = getDiagnosticStartIndex(transcript);
    const diagnosticTranscript = transcript.slice(diagnosticStartIndex);
    const vehicle = cleanText(vehicleLabel) || 'Selected vehicle';
    const issue = getIssueFromTranscript(transcript);
    const details = getQuestionAnswers(diagnosticTranscript);
    const createdAt = new Date();
    const category = classifyDiagnosticCategory(issue, details);
    const assessment = category === 'no_start'
        ? buildNoStartCauses(vehicle, issue, details)
        : category === 'ac_cooling'
        ? buildAcCoolingAssessment(vehicle, issue, details)
        : category === 'door_lock'
        ? buildDoorLockAssessment(vehicle, issue, details)
        : category === 'wheel_rotation'
        ? buildWheelRotationAssessment(vehicle, issue, details)
        : category === 'brake'
        ? buildBrakeAssessment(vehicle, issue, details)
        : buildGeneralAssessment(vehicle, issue, details);

    return {
        id: reportMessage?.id || `diagnostic-${createdAt.getTime()}`,
        issue,
        vehicle,
        status: 'Ready for Review',
        createdLabel: formatDate(createdAt),
        sourceUrl: reportMessage?.pdfUrl || null,
        symptoms: [issue],
        details,
        diagnosisStage: 'Diagnosis',
        completedGuidedChecks: [],
        ...assessment,
    };
};

// ── Scheduled-maintenance DIY report ─────────────────────────────────────────
// A maintenance item (from the Garage "Ask CarFido" flow) is NOT a fault, so it does not
// run through fault classification. When the user chooses to do it themselves we
// synthesize a report whose single "likely cause" IS the service: whyItFits = why it
// matters, repairSteps = the DIY procedure. The report card is fault-agnostic, so it
// renders the steps and brings the Find Parts / Find a Shop actions for free.
const MAINTENANCE_LIBRARY = [
    {
        match: /cabin air filter|cabin filter|ac filter|pollen filter/i,
        parts: ['Cabin air filter'],
        difficulty: 'Easy',
        diyCost: '$15-$35',
        time: '10-20 min',
        tools: ['Clean cloth'],
        why: 'The cabin air filter cleans the air coming in through the vents; a clogged one weakens airflow and causes musty smells and window fogging.',
        steps: [
            { instruction: 'Empty and lower the glove box', detail: 'Open the glove box, take everything out, then squeeze its sides in (or release the stopper) so it drops down and exposes the filter door behind it.' },
            { instruction: 'Open the filter door and note the airflow arrow', detail: 'Unclip the small cover and slide the old filter out. Notice the up/down airflow arrow printed on it so the new one goes in the same way.' },
            { instruction: 'Fit the new filter', detail: 'Slide the new cabin air filter in with its airflow arrow pointing the same direction as the old one.' },
            { instruction: 'Close everything back up', detail: 'Refit the filter cover, then lift the glove box back into place and click the stoppers back in.' },
        ],
    },
    {
        match: /engine air filter|air filter|air cleaner|intake filter/i,
        parts: ['Engine air filter'],
        difficulty: 'Easy',
        diyCost: '$15-$40',
        time: '10-15 min',
        tools: ['Flat-head screwdriver (if clips are tight)'],
        why: 'The engine air filter keeps dust out of the engine; a clogged one restricts airflow and can hurt fuel economy and throttle response.',
        steps: [
            { instruction: 'Locate the air box', detail: 'Open the hood and find the black plastic air box, usually on one side of the engine bay with a large duct running to it.' },
            { instruction: 'Open the air box', detail: 'Release the metal clips or screws holding the lid and lift it enough to reach the filter inside.' },
            { instruction: 'Swap the filter', detail: 'Note how the old filter sits, lift it out, wipe out any debris in the box, and drop the new filter in the same orientation.' },
            { instruction: 'Close the air box', detail: 'Reseat the lid, refasten every clip/screw, and make sure the intake duct is fully seated.' },
        ],
    },
    {
        match: /oil (change|and filter|& filter|service)|engine oil/i,
        parts: ['Engine oil (correct grade)', 'Oil filter', 'Drain plug washer'],
        difficulty: 'Medium',
        diyCost: '$35-$80',
        time: '30-60 min',
        tools: ['Socket wrench', 'Oil filter wrench', 'Drain pan', 'Funnel', 'Jack and jack stands'],
        why: 'Fresh oil and a new filter protect the engine from wear and heat; old, dirty oil loses its ability to lubricate and cool.',
        steps: [
            { instruction: 'Warm the engine and lift the car safely', detail: 'Run the engine for a minute so the oil flows, shut it off, then raise the front on jack stands on level ground — never work under a car held only by a jack.' },
            { instruction: 'Drain the old oil', detail: 'Place the drain pan under the sump, remove the drain plug, and let all the old oil drain out fully.' },
            { instruction: 'Replace the oil filter', detail: 'Unscrew the old filter (oil will spill), lightly oil the new filter’s gasket, and hand-tighten the new filter into place.' },
            { instruction: 'Refit the plug and refill', detail: 'Fit a new washer and the drain plug to spec, then add the correct amount and grade of new oil through the filler cap.' },
            { instruction: 'Check the level and for leaks', detail: 'Run the engine briefly, shut it off, wait a minute, and check the dipstick reads FULL and there are no leaks at the plug or filter.' },
        ],
    },
    {
        match: /tire rotation|tyre rotation|rotate (the )?(tires|tyres)/i,
        parts: [],
        difficulty: 'Medium',
        diyCost: '$0 (DIY) / $20-$50 (shop)',
        time: '30-45 min',
        tools: ['Jack and jack stands', 'Lug wrench', 'Torque wrench'],
        why: 'Front tires wear faster because they handle steering and most braking; rotating evens out wear so the set lasts longer and grips better.',
        steps: [
            { instruction: 'Loosen the lug nuts first', detail: 'With the car still on the ground, crack each wheel’s lug nuts loose about a quarter turn (do not remove them yet).' },
            { instruction: 'Lift and support the car', detail: 'Raise the car and set it on jack stands on level ground so the wheels you are moving are clear of the ground safely.' },
            { instruction: 'Move the tires to the correct pattern', detail: 'For most front-wheel-drive cars: fronts go straight back, and rears cross to the opposite front. Follow your manual if the tires are directional.' },
            { instruction: 'Torque the lug nuts in a star pattern', detail: 'Lower the car and tighten the lug nuts to the specified torque in a crisscross pattern so the wheel seats evenly.' },
            { instruction: 'Check tire pressure', detail: 'Set each tire to the pressure on the driver’s door-jamb sticker, since it can differ front vs rear.' },
        ],
    },
    {
        match: /wiper|wiper blade/i,
        parts: ['Wiper blades (correct sizes)'],
        difficulty: 'Easy',
        diyCost: '$15-$40',
        time: '5-10 min',
        tools: [],
        why: 'Worn wiper blades streak and smear, cutting visibility in rain; they are a cheap, quick swap.',
        steps: [
            { instruction: 'Lift the wiper arm', detail: 'Pull the wiper arm away from the glass so it stands upright and locks.' },
            { instruction: 'Release the old blade', detail: 'Press the small tab where the blade meets the arm and slide the old blade off the hook.' },
            { instruction: 'Clip on the new blade', detail: 'Slide the new blade onto the arm until it clicks, then gently lower the arm back onto the glass.' },
            { instruction: 'Test both wipers', detail: 'Run the wipers with washer fluid to confirm a clean, streak-free wipe.' },
        ],
    },
    {
        match: /brake fluid/i,
        parts: ['Brake fluid (correct DOT spec)'],
        difficulty: 'Hard',
        diyCost: '$15-$40 (DIY) / $80-$150 (shop)',
        time: '45-90 min',
        tools: ['Wrench for bleeder valves', 'Clear tubing', 'Catch bottle', 'Jack and jack stands', 'Helper or one-person bleeder kit'],
        why: 'Brake fluid absorbs moisture over time, which lowers its boiling point and can make the pedal feel soft; fresh fluid restores firm, reliable braking.',
        steps: [
            { instruction: 'Confirm the correct fluid and lift the car', detail: 'Check the cap/manual for the exact DOT spec, then raise and support the car on jack stands on level ground.' },
            { instruction: 'Top up the reservoir', detail: 'Clean around the master-cylinder cap, open it, and fill to MAX so it never runs dry while bleeding.' },
            { instruction: 'Bleed each corner in order', detail: 'Starting at the wheel farthest from the master cylinder, attach tubing to the bleeder, have a helper hold the pedal, open the valve to release old fluid, then close it before the pedal is released. Repeat until fluid runs clean.' },
            { instruction: 'Keep the reservoir topped up', detail: 'Refill the reservoir after each corner so air is never drawn into the system.' },
            { instruction: 'Test the pedal before driving', detail: 'With everything closed and the reservoir at MAX, confirm the pedal is firm and check for leaks before driving.' },
        ],
        safety: 'Brakes are safety-critical. If the pedal is soft or you are unsure, stop and have a professional finish the job before driving.',
    },
    {
        match: /coolant|antifreeze|radiator (flush|fluid)/i,
        parts: ['Coolant (correct type)', 'Distilled water'],
        difficulty: 'Medium',
        diyCost: '$20-$50',
        time: '45-60 min',
        tools: ['Drain pan', 'Funnel', 'Gloves', 'Jack and jack stands'],
        why: 'Coolant carries heat away from the engine and resists corrosion; old coolant loses protection and can let the engine overheat.',
        steps: [
            { instruction: 'Only work on a COLD engine', detail: 'Never open a hot cooling system — wait until the engine is fully cool so the system is not under pressure.' },
            { instruction: 'Drain the old coolant', detail: 'Place a pan under the radiator drain (petcock), open it, and let the old coolant drain completely; collect it for proper disposal.' },
            { instruction: 'Refill with the correct mix', detail: 'Close the drain and fill with the specified coolant (usually a 50/50 mix with distilled water) up to the marked level.' },
            { instruction: 'Bleed the air out', detail: 'Run the engine with the cap off (per your manual) until it warms up and the level settles, topping up as trapped air escapes.' },
            { instruction: 'Cap and check', detail: 'Fit the cap, check the overflow bottle is between MIN and MAX, and look for leaks once it reaches temperature.' },
        ],
        safety: 'Coolant is hot and pressurised when the engine is warm and is toxic to pets — only work cold and clean up spills.',
    },
    {
        match: /spark plug/i,
        parts: ['Spark plugs (correct type, gapped)'],
        difficulty: 'Medium',
        diyCost: '$30-$90',
        time: '45-90 min',
        tools: ['Spark plug socket', 'Ratchet and extension', 'Torque wrench', 'Gap gauge'],
        why: 'Spark plugs ignite the fuel; worn plugs cause rough idle, misfires, and poorer fuel economy.',
        steps: [
            { instruction: 'Work on a cool engine', detail: 'Let the engine cool so you do not damage the threads in the aluminium head.' },
            { instruction: 'Remove one coil/wire at a time', detail: 'Disconnect and remove the coil pack or plug wire from the first cylinder only, so you never mix up the firing order.' },
            { instruction: 'Swap that plug', detail: 'Use the spark plug socket to remove the old plug, check the new plug’s gap, and thread the new one in by hand first to avoid cross-threading.' },
            { instruction: 'Torque and reconnect', detail: 'Tighten the plug to the specified torque, refit the coil/wire, and move to the next cylinder — one at a time.' },
            { instruction: 'Start and confirm smooth running', detail: 'Once all plugs are done, start the engine and confirm it idles smoothly with no misfire.' },
        ],
    },
    {
        match: /battery/i,
        parts: ['Battery (correct group size)'],
        difficulty: 'Easy',
        diyCost: '$120-$250',
        time: '20-30 min',
        tools: ['Wrench for terminals', 'Wire brush', 'Gloves'],
        why: 'The battery starts the car and steadies electrical power; a weak or old battery causes slow cranking and no-starts, especially in cold weather.',
        steps: [
            { instruction: 'Disconnect negative first', detail: 'With the car off, loosen and remove the negative (−) terminal first, then the positive (+), to avoid shorting.' },
            { instruction: 'Remove the hold-down and battery', detail: 'Undo the bracket holding the battery down and lift it straight out (they are heavy).' },
            { instruction: 'Clean and fit the new battery', detail: 'Wire-brush any corrosion off the terminals, set the new battery in the same orientation, and refit the hold-down.' },
            { instruction: 'Reconnect positive first', detail: 'Attach the positive (+) terminal first, then the negative (−), and make sure both are snug.' },
            { instruction: 'Confirm it starts', detail: 'Start the car to confirm strong, quick cranking and reset the clock/radio if needed.' },
        ],
    },
    {
        match: /transmission fluid|gearbox (fluid|oil)|atf/i,
        parts: ['Transmission fluid (exact spec)', 'Filter/gasket (if serviceable)'],
        difficulty: 'Hard',
        diyCost: '$50-$120 (DIY) / $150-$300 (shop)',
        time: '60-120 min',
        tools: ['Socket set', 'Drain pan', 'Fluid pump', 'Jack and jack stands'],
        why: 'Transmission fluid lubricates and cools the gearbox; degraded fluid causes rough or delayed shifts and long-term wear.',
        steps: [
            { instruction: 'Use the EXACT specified fluid', detail: 'Transmissions are picky — confirm the exact fluid spec in your manual before buying; the wrong type can damage the gearbox.' },
            { instruction: 'Lift the car level', detail: 'Raise and support the car level on jack stands so the fluid level reads correctly.' },
            { instruction: 'Drain and (if serviceable) drop the pan', detail: 'Drain the old fluid; on models with a serviceable pan, remove it and replace the filter and gasket.' },
            { instruction: 'Refill to spec', detail: 'Pump in the correct amount of the exact fluid through the fill port; many are filled to a level plug, not a dipstick.' },
            { instruction: 'Check level at temperature', detail: 'Follow the manual’s procedure to check the level at the right fluid temperature and gear, then look for leaks.' },
        ],
    },
];

const GENERIC_MAINTENANCE = (title) => {
    const lower = (title || 'this service').toLowerCase();
    return {
        parts: [],
        difficulty: 'Medium',
        diyCost: 'Varies',
        time: '30-60 min',
        tools: ['Basic hand tools', 'Gloves', 'Flashlight'],
        why: `${title} is part of your vehicle's scheduled maintenance; keeping up with it prevents avoidable wear and keeps the system working reliably.`,
        steps: [
            { instruction: 'Confirm the part and interval for your vehicle', detail: `Check your owner's manual for the correct ${lower} part/spec and the recommended interval before you start.` },
            { instruction: 'Prepare the vehicle safely', detail: 'Park on level ground with the engine off (and cool if it involves the engine bay). If any lifting is needed, use jack stands — never rely on the jack alone.' },
            { instruction: 'Access and inspect the component', detail: `Locate the component involved in the ${lower} and inspect its condition and how it mounts before removing anything.` },
            { instruction: 'Perform the service', detail: 'Fit the correct part or fresh fluid, matching orientation, torque, and level where relevant.' },
            { instruction: 'Reassemble and verify', detail: 'Put everything back together, then confirm it is secure and working correctly before driving.' },
        ],
    };
};

// Pull the maintenance item's name out of the Garage "Ask CarFido" question
// ("...what is the Cabin Air Filter service on my 2020 Toyota Camry...").
export const extractMaintenanceTitleFromMessages = (messages = []) => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const m = messages[i];
        if (m?.role !== 'user') continue;
        const c = cleanText(m.content);
        const match = c.match(/what is the\s+(.+?)\s+service\b/i);
        if (match?.[1]) return match[1].trim();
    }
    return '';
};

export const createMaintenanceReport = ({ messages = [], vehicleLabel, maintenanceTitle = '', guide = null } = {}) => {
    const vehicle = cleanText(vehicleLabel) || 'Selected vehicle';
    const rawTitle = cleanText(maintenanceTitle) || extractMaintenanceTitleFromMessages(messages) || 'Scheduled maintenance';
    // Prefer the LLM-generated, vehicle-tailored guide. The built-in library/generic entry is only
    // a FALLBACK for when the model call is unavailable or returns nothing usable.
    const libSpec = MAINTENANCE_LIBRARY.find((entry) => entry.match.test(rawTitle)) || GENERIC_MAINTENANCE(rawTitle);
    const hasGuide = guide && Array.isArray(guide.steps) && guide.steps.length > 0;
    const spec = hasGuide
        ? {
            parts: Array.isArray(guide.parts) && guide.parts.length ? guide.parts : libSpec.parts,
            difficulty: guide.difficulty || libSpec.difficulty,
            diyCost: guide.diyCost || libSpec.diyCost,
            time: guide.time || libSpec.time,
            tools: Array.isArray(guide.tools) && guide.tools.length ? guide.tools : libSpec.tools,
            why: guide.whyItMatters || libSpec.why,
            steps: guide.steps
                .map((s) => (typeof s === 'string'
                    ? { instruction: cleanText(s), detail: '' }
                    : { instruction: cleanText(s?.instruction || s?.title || ''), detail: cleanText(s?.detail || s?.description || '') }))
                .filter((s) => s.instruction),
            safety: guide.safety || libSpec.safety,
        }
        : libSpec;
    const createdAt = new Date();
    const serviceLabel = /\b(service|change|rotation|replacement|flush|inspection)\b/i.test(rawTitle)
        ? rawTitle
        : `${rawTitle} service`;

    const cause = {
        ...createCause({
            vehicle,
            title: serviceLabel,
            score: 90,
            recommendedCheck: `Do the ${rawTitle}`,
            checkDescription: spec.why,
            toolSummary: (spec.tools || []).join(', '),
            timeLabel: spec.time,
            expectedResult: 'Completing this routine service keeps the system working properly and prevents avoidable wear.',
            requiredTools: (spec.tools || []).map((t) => createTool(t)),
            requiredParts: (spec.parts || []).map((p) => createPart(p)),
            likelyRepair: serviceLabel,
            diyRepairCost: spec.diyCost,
            repairTime: spec.time,
            diyDifficulty: spec.difficulty,
            safetyPrecautions: spec.safety ? [spec.safety] : [],
            repairSteps: spec.steps,
            whyItFits: spec.why,
        }),
        kindLabel: 'Recommended Service',
    };

    return {
        id: `maintenance-${slugify(rawTitle)}-${createdAt.getTime()}`,
        issue: serviceLabel,
        vehicle,
        status: 'Ready for Review',
        createdLabel: formatDate(createdAt),
        sourceUrl: null,
        symptoms: [serviceLabel],
        details: [],
        diagnosisStage: 'Maintenance',
        completedGuidedChecks: [],
        severity: 'Routine Maintenance',
        safeToDrive: 'Yes',
        safetyNote: spec.safety || 'This is scheduled maintenance, not a fault. Work with the engine off and the car on level ground.',
        canDriveMessage: 'This is routine scheduled maintenance — the vehicle is safe to drive in the meantime.',
        canDriveTimeframe: 'Do it at your convenience.',
        summary: `${vehicle}: ${serviceLabel} is routine maintenance. Here is how to do it yourself, with parts and a nearby shop below if you prefer.`,
        readinessLabel: 'Ready for Action',
        symptomsDetailed: [createSymptomBullet(spec.why)],
        likelyCauses: [cause],
        recommendedChecks: [cause.recommendedCheck],
        mechanicSummary: `${vehicle}: ${serviceLabel} — routine service. DIY steps included; a shop can also perform it.`,
    };
};

const normalizeRecommendedCheck = (recommendedCheck, cause = {}) => {
    if (recommendedCheck && typeof recommendedCheck === 'object') {
        return {
            text: cleanText(recommendedCheck.title || cause.checkTitle || ''),
            description: cleanText(recommendedCheck.description || cause.checkDescription || ''),
            toolsLabel: cleanText(recommendedCheck.toolsLabel || cause.toolSummary || ''),
            duration: cleanText(recommendedCheck.duration || cause.timeLabel || ''),
            expectedResult: cleanText(recommendedCheck.expectedResult || cause.expectedResult || ''),
            requiredTools: Array.isArray(recommendedCheck.requiredTools) ? recommendedCheck.requiredTools : [],
            requiredParts: Array.isArray(recommendedCheck.requiredParts) ? recommendedCheck.requiredParts : [],
        };
    }

    return {
        text: cleanText(recommendedCheck || cause.checkTitle || ''),
        description: cleanText(cause.checkDescription || ''),
        toolsLabel: cleanText(cause.toolSummary || ''),
        duration: cleanText(cause.timeLabel || ''),
        expectedResult: cleanText(cause.expectedResult || ''),
        requiredTools: Array.isArray(cause.requiredTools) ? cause.requiredTools : [],
        requiredParts: Array.isArray(cause.requiredParts) ? cause.requiredParts : [],
    };
};

const normalizeGuidedSteps = (guidedSteps = [], toolLabel = '') => {
    if (!Array.isArray(guidedSteps)) return [];
    return guidedSteps
        .map((step) => {
            if (!step) return null;
            if (typeof step === 'string') {
                return createGuidedStep(step, '', toolLabel);
            }
            return createGuidedStep(step.instruction || step.title || '', step.detail || step.description || '', step.toolLabel || toolLabel || '');
        })
        .filter((step) => cleanText(step?.instruction));
};

const normalizeQuestionAnswerItems = (items = []) => {
    if (!Array.isArray(items)) return [];
    return items
        .map((item, index) => {
            const source = item && typeof item === 'object' ? item : {};
            const rawQuestion = source.question || source.prompt || source.label || source.title || '';
            const rawAnswer = source.answer || source.response || source.value || source.text || (typeof item === 'string' ? item : '');
            const answerHadAttachmentMarker = String(rawAnswer || '').includes(ATTACHMENT_MARKER_PREFIX);
            const question = cleanText(rawQuestion);
            const answer = cleanText(rawAnswer);

            if (!answer && answerHadAttachmentMarker) return null;
            if (!question && !answer) return null;

            return {
                ...source,
                question: question || `Question ${index + 1}`,
                answer: answer || 'No answer captured.',
            };
        })
        .filter(Boolean);
};

export const normalizeDiagnosticReportForUi = (report) => {
    if (!report || typeof report !== 'object') return report;

    const likelyCauses = Array.isArray(report.likelyCauses)
        ? report.likelyCauses.map((cause, index) => {
            const normalizedCheck = normalizeRecommendedCheck(cause?.recommendedCheck, cause || {});
            const requiredTools = (Array.isArray(cause?.requiredTools) && cause.requiredTools.length > 0)
                ? cause.requiredTools
                : normalizedCheck.requiredTools;
            const requiredParts = (Array.isArray(cause?.requiredParts) && cause.requiredParts.length > 0)
                ? cause.requiredParts
                : normalizedCheck.requiredParts;
            const toolSummary = cleanText(
                cause?.toolSummary ||
                normalizedCheck.toolsLabel ||
                requiredTools.map((tool) => tool?.name || tool).filter(Boolean).join(', ')
            );

            return {
                ...cause,
                id: cause?.id || `cause-${index + 1}`,
                confidenceScore: Number.isFinite(Number(cause?.confidenceScore))
                    ? Number(cause.confidenceScore)
                    : clampScore(cause?.score ?? 65),
                whyItFits: cleanText(cause?.whyItFits || cause?.why || ''),
                diyRepairCost: cleanText(cause?.diyRepairCost || cause?.diyCost || ''),
                diyDifficulty: normalizeDifficulty(cause?.diyDifficulty || cause?.difficulty || 'Medium'),
                recommendedCheck: normalizedCheck.text,
                checkDescription: normalizedCheck.description,
                toolSummary,
                timeLabel: cleanText(cause?.timeLabel || normalizedCheck.duration || ''),
                expectedResult: cleanText(cause?.expectedResult || normalizedCheck.expectedResult || ''),
                requiredTools,
                requiredParts,
                guidedSteps: normalizeGuidedSteps(cause?.guidedSteps, toolSummary),
                repairSteps: buildDetailedRepairSteps({
                    repairSteps: Array.isArray(cause?.repairSteps) ? cause.repairSteps : [],
                    guidedSteps: normalizeGuidedSteps(cause?.guidedSteps, toolSummary),
                    steps: Array.isArray(cause?.steps) ? cause.steps : [],
                }),
                safetyPrecautions: Array.isArray(cause?.safetyPrecautions) ? cause.safetyPrecautions : [],
                recommendedCheckDetail: normalizedCheck,
            };
        })
        : [];

    const normalizedReport = {
        ...report,
        likelyCauses,
        details: normalizeQuestionAnswerItems(report.details),
    };

    if (Array.isArray(report.diagnosticQuestionAnswers)) {
        normalizedReport.diagnosticQuestionAnswers = normalizeQuestionAnswerItems(report.diagnosticQuestionAnswers);
    }
    if (Array.isArray(report.diagnostic_questions)) {
        normalizedReport.diagnostic_questions = normalizeQuestionAnswerItems(report.diagnostic_questions);
    }

    if (!Array.isArray(normalizedReport.recommendedChecks) || normalizedReport.recommendedChecks.some((item) => typeof item !== 'string')) {
        normalizedReport.recommendedChecks = likelyCauses
            .map((cause) => (
                cause.expectedResult
                    ? `${cause.recommendedCheck} — ${cause.expectedResult}`
                    : cause.recommendedCheck
            ))
            .filter(Boolean);
    }

    return normalizedReport;
};

export const saveDiagnosticReport = (report) => {
    if (!report?.id) return;
    const key = `dash_diagnostic_report:${report.id}`;
    const payload = JSON.stringify(normalizeDiagnosticReportForUi(report));
    try {
        sessionStorage.setItem(key, payload);
    } catch {}
    try {
        localStorage.setItem(key, payload);
    } catch {}
};

export const loadDiagnosticReport = (id) => {
    if (!id) return null;
    try {
        const key = `dash_diagnostic_report:${id}`;
        const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
        return raw ? normalizeDiagnosticReportForUi(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
};
